import checkVisitorStatus from './check-visitor-status.js'

function utmTracking() {
  const utmParams = new URLSearchParams(window.location.search)
  const utmParamsArray = Array.from(utmParams.entries())
    .filter((param) => param[0].includes('utm_'))
    .map((param) => {
      // Decode URL-encoded characters, remove all non-alphanumeric characters
      const decoded = decodeURIComponent(param[1])
      const sanitized = decoded.replace(/[^a-zA-Z0-9]/g, '') // Remove all non-alphanumeric characters
      return `${param[0].replace(/[^a-zA-Z0-9]/g, '')}${sanitized}`
    })

  if (utmParamsArray.length > 0) {
    // Store in sessionStorage
    sessionStorage.setItem('utmParams', JSON.stringify(utmParamsArray))
    return utmParamsArray
  } else {
    // Try to retrieve from sessionStorage
    const stored = sessionStorage.getItem('utmParams')
    return stored ? JSON.parse(stored) : []
  }
}

const utmParams = utmTracking()
let visitorStatus = ''

/**
 * Product card images were being served at their master resolution (measured:
 * 1280x1600 painted into a 307px slot) because the Algolia app's
 * `helpers.originalImage()` returns the unresized Shopify CDN URL and the card
 * markup carried no srcset. That is wasted bytes and, more importantly, wasted
 * main-thread decode time while the shopper is scrolling — an INP cost.
 *
 * These helpers only rewrite Shopify CDN URLs. Anything else (Gadget-hosted
 * registry images, external assets) is returned untouched.
 */
const CARD_IMAGE_WIDTHS = [200, 300, 400, 600, 800]

// Grid is roughly 2-up on phones, 3-up on tablets, 4-up on desktop. Overshooting
// is the safe direction — the browser picks the next step up, never a blurry one.
const CARD_IMAGE_SIZES = '(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw'

function isShopifyCdnUrl(url) {
  return typeof url === 'string' && (url.indexOf('cdn.shopify.com') > -1 || url.indexOf('/cdn/shop/') > -1)
}

function resizedImage(url, width) {
  if (!isShopifyCdnUrl(url)) return url
  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.set('width', String(width))
    return parsed.toString()
  } catch (e) {
    // Malformed or relative URL — leave it alone rather than risk a broken src.
    return url
  }
}

function cardImageSrcset(url) {
  if (!isShopifyCdnUrl(url)) return ''
  return CARD_IMAGE_WIDTHS.map((w) => `${resizedImage(url, w)} ${w}w`).join(', ')
}

// Hover images are revealed by :hover, so on touch devices they are downloaded
// and decoded for an interaction that can never happen. ~90% of this store's
// sessions are mobile. Revert by making this always return true.
function supportsHover() {
  return typeof window.matchMedia === 'function' ? window.matchMedia('(hover: hover)').matches : true
}

const matches = window.location.pathname.match(/\/collections\/([^/]+)/i)
const collectionHandle = matches && matches.length === 2 ? matches[1] : null
const searchPage = window.location.pathname.match(/\/search/i)
let pageHandle = null
if (collectionHandle) {
  pageHandle = collectionHandle
} else if (searchPage) {
  pageHandle = 'search-results-page'
}
const analyticsTags = [pageHandle, visitorStatus, ...utmParams]
const BASE_PRODUCT_FILTER = 'available:true AND NOT tags:hidden'

const getCookieValue = (name) => {
  const regex = new RegExp(`(^| )${name}=([^;]+)`)
  const match = document.cookie.match(regex)
  if (match) {
    return match[2]
  }
}

function handleize(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

const HEADER_FILTER_RESULTS_STATUS_ID = 'algolia-header-filter-results-status'
let headerFilterResultsObserver
let headerFilterResultsAnnouncementFrame
let headerFilterResultsAnnouncementTimeout
let headerFilterResultsBindingsInitialized = false

const COLLECTION_FILTER_RESULTS_STATUS_ID = 'algolia-collection-filter-results-status'
let pendingCollectionFilterAnnouncement = false

function ensureCollectionFilterStatusRegion() {
  let statusRegion = document.getElementById(COLLECTION_FILTER_RESULTS_STATUS_ID)
  if (statusRegion) return statusRegion

  statusRegion = document.createElement('div')
  statusRegion.id = COLLECTION_FILTER_RESULTS_STATUS_ID
  statusRegion.className = 'sr-only'
  statusRegion.setAttribute('aria-live', 'assertive')
  statusRegion.setAttribute('aria-atomic', 'true')
  document.body.appendChild(statusRegion)

  return statusRegion
}

function announceCollectionFilterResults() {
  const statusRegion = ensureCollectionFilterStatusRegion()
  const hitsContainer = document.querySelector('.ais-hits-container')

  if (!hitsContainer) return

  const emptyState = hitsContainer.querySelector('.ais-hit-empty')
  if (emptyState) {
    statusRegion.textContent = 'No results available'
    return
  }

  const count = hitsContainer.querySelectorAll('.ais-Hits-item').length
  statusRegion.textContent = count === 1 ? '1 result available' : `${count} results available`
}

function getVisibleAutocompleteItemCount(scopeElement = document) {
  const items = Array.from(scopeElement.querySelectorAll('a.ais-hit.ais-product'))
  return items.filter((item) => {
    return isElementVisible(item)
  }).length
}

function ensureHeaderFilterStatusRegion() {
  let statusRegion = document.getElementById(HEADER_FILTER_RESULTS_STATUS_ID)
  if (statusRegion) return statusRegion

  statusRegion = document.createElement('div')
  statusRegion.id = HEADER_FILTER_RESULTS_STATUS_ID
  statusRegion.className = 'sr-only'
  statusRegion.setAttribute('aria-live', 'polite')
  statusRegion.setAttribute('aria-atomic', 'true')

  const headerSearchContainer = document.querySelector('[data-algolia-search-container]')
  if (headerSearchContainer) {
    headerSearchContainer.appendChild(statusRegion)
  } else {
    document.body.appendChild(statusRegion)
  }

  return statusRegion
}

function isElementVisible(element) {
  if (!element) return false
  if (element.hidden) return false
  if (element.closest('[hidden], .hidden, [aria-hidden="true"]')) return false

  const style = window.getComputedStyle(element)
  if (style.display === 'none' || style.visibility === 'hidden') return false

  if (element.getClientRects().length > 0) return true

  return style.position === 'fixed'
}

function getOpenHeaderAutocompletePanel(activeSearchInput) {
  if (!activeSearchInput) return false

  const inputAutocomplete = activeSearchInput.closest('.aa-Autocomplete')
  const inputPanel = inputAutocomplete ? inputAutocomplete.querySelector('.aa-Panel') : null
  if (isElementVisible(inputPanel)) return inputPanel

  const detachedOverlay = document.querySelector('.aa-DetachedOverlay')
  const detachedPanel = detachedOverlay ? detachedOverlay.querySelector('.aa-Panel') : null
  if (isElementVisible(detachedPanel)) return detachedPanel

  return null
}

function updateHeaderFilterResultsAnnouncement() {
  const statusRegion = ensureHeaderFilterStatusRegion()
  const activeSearchInput =
    document.querySelector('input.aa-Input[type="search"]:focus') ||
    document.querySelector('input.aa-Input[type="search"]')

  if (!activeSearchInput) {
    statusRegion.textContent = ''
    return
  }

  activeSearchInput.setAttribute('aria-label', 'Filter products')
  activeSearchInput.setAttribute('aria-describedby', HEADER_FILTER_RESULTS_STATUS_ID)

  const query = activeSearchInput.value.trim()
  if (!query) {
    statusRegion.textContent = ''
    return
  }

  const openPanel = getOpenHeaderAutocompletePanel(activeSearchInput)
  const inputIsFocused = document.activeElement === activeSearchInput
  if (!openPanel && !inputIsFocused) {
    statusRegion.textContent = ''
    return
  }

  const visibleCount = getVisibleAutocompleteItemCount(openPanel || document)
  statusRegion.textContent = `${visibleCount} ${visibleCount === 1 ? 'result' : 'results'} after filtering`
}

function scheduleHeaderFilterResultsAnnouncement() {
  if (headerFilterResultsAnnouncementFrame) {
    cancelAnimationFrame(headerFilterResultsAnnouncementFrame)
  }

  clearTimeout(headerFilterResultsAnnouncementTimeout)

  headerFilterResultsAnnouncementFrame = requestAnimationFrame(() => {
    updateHeaderFilterResultsAnnouncement()
    headerFilterResultsAnnouncementTimeout = setTimeout(updateHeaderFilterResultsAnnouncement, 120)
  })
}

function initHeaderFilterResultAnnouncements() {
  if (headerFilterResultsBindingsInitialized) return

  ensureHeaderFilterStatusRegion()

  const bindSearchInputs = () => {
    document.querySelectorAll('input.aa-Input[type="search"]').forEach((input) => {
      if (input.dataset.headerFilterA11yBound === 'true') return

      input.dataset.headerFilterA11yBound = 'true'
      input.setAttribute('aria-label', 'Filter products')
      input.setAttribute('aria-describedby', HEADER_FILTER_RESULTS_STATUS_ID)
      input.addEventListener('input', scheduleHeaderFilterResultsAnnouncement)
      input.addEventListener('keyup', scheduleHeaderFilterResultsAnnouncement)
      input.addEventListener('focus', scheduleHeaderFilterResultsAnnouncement)
      input.addEventListener('change', scheduleHeaderFilterResultsAnnouncement)
    })
  }

  bindSearchInputs()
  scheduleHeaderFilterResultsAnnouncement()

  document.addEventListener('focusin', (event) => {
    if (!event.target || !event.target.matches || !event.target.matches('input.aa-Input[type="search"]')) return
    bindSearchInputs()
    scheduleHeaderFilterResultsAnnouncement()
  })

  headerFilterResultsObserver = true
  headerFilterResultsBindingsInitialized = true
}

let isMobile = window.innerWidth < 681

window.addEventListener('resize', () => {
  isMobile = window.innerWidth < 681
})

const colors = {
  'off-white': {
    name: 'Off-White',
    hex: '#E5DFDD',
  },
  yellow: {
    name: 'Yellow',
    hex: '#FAEB7A',
  },
  white: {
    name: 'White',
    hex: '#FDFEFE',
  },
  tan: {
    name: 'Tan',
    hex: '#D2B48C',
  },
  stone: {
    name: 'Stone',
    hex: '#878787',
  },
  silver: {
    name: 'Silver',
    hex: '#B4B4B4',
  },
  red: {
    name: 'Red',
    hex: '#E73A35',
  },
  purple: {
    name: 'Purple',
    hex: '#C19FC6',
  },
  pink: {
    name: 'Pink',
    hex: '#F8C6CC',
  },
  pearl: {
    name: 'Pearl',
    hex: '#EDEEF1',
  },
  orange: {
    name: 'Orange',
    hex: '#FFCC33',
  },
  multi: {
    name: 'Multi',
    image: 'https://cdn.shopify.com/s/files/1/0039/6479/7046/files/multi-swatch.webp?v=1716566769',
  },
  green: {
    name: 'Green',
    hex: '#9BC7AC',
  },
  gray: {
    name: 'Gray',
    hex: '#999999',
  },
  blue: {
    name: 'Blue',
    hex: '#9CD7F6',
  },
  black: {
    name: 'Black',
    hex: '#100F0F',
  },
  navy: {
    name: 'Navy',
    hex: '#141f52',
  },
}
const fabricIcons = {
  'gid://shopify/Metaobject/65274478710': {
    name: 'Lunaluxe',
    image: 'https://cdn.shopify.com/s/files/1/0039/6479/7046/files/lunaluxe.png?v=1720190769',
  },
  'gid://shopify/Metaobject/65274904694': {
    name: 'Soluxe',
    image: 'https://cdn.shopify.com/s/files/1/0039/6479/7046/files/soluxe.png?v=1726082666',
  },
}
const badges = {
  'gid://shopify/Metaobject/87399465318': {
    content: 'New',
    background_color: '#BAD7DC',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87399498086': {
    content: 'Best Seller',
    background_color: '#8EB4E0',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87399530854': {
    content: 'Bundle & Save',
    background_color: '#FBD9D1',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87399563622': {
    content: '3 for 20% Off',
    background_color: '#FCE2AC',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87399596390': {
    content: 'Great Gift',
    background_color: '#C7D9EF',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87399629158': {
    content: 'Trending',
    background_color: '#9DC1A8',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/87573201254': {
    content: '3 for 25% Off!',
    background_color: '#FCE2AC',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/68925554806': {
    content: 'Glows in the Dark!',
    background_color: '#403940',
    text_color: '#FFFFFF',
    text_color_animated: '#39FF14',
    animate_text_color: true,
  },
  'gid://shopify/Metaobject/87863591270': {
    content: '100% Organic Cotton',
    background_color: '#E2ECE5',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/88042242406': {
    content: 'Personalize Me!',
    background_color: '#E2ECE5',
    text_color: '#3D3C3D',
  },
  'gid://shopify/Metaobject/88064491878': {
    content: 'Super Savings!',
    background_color: '#FBD9D1',
    text_color: '#3D3C3D',
  },
}
const promoCodes = {
  'gid://shopify/Metaobject/87072244070': {
    text: '40% off',
    code: 'COMFY',
    color: '#EC65C1',
    discount_percent: 60,
  },
  'gid://shopify/Metaobject/87072833894': {
    text: '30% off',
    code: 'COMFY',
    color: '#FAD588',
    discount_percent: 70,
  },
  'gid://shopify/Metaobject/87100817766': {
    text: 'Excluded from BFCM',
    color: '#90BFAF',
  },
  'gid://shopify/Metaobject/87102652774': {
    text: 'Extra 30% off!',
    code: 'COMFY',
    color: '#BB256B',
    discount_percent: 70,
  },
}

const staticLinks = (html) => {
  return html`
    <div class="p-4 xl:p-0">
      <h2
        id="support-nav-heading"
        class="holiday-text-primary mb-4 !font-galano text-xs font-bold leading-tight text-galaxy xl:mt-8 xl:text-sm"
      >
        How can we help?
      </h2>
      <nav
        aria-labelledby="support-nav-heading"
        class="w-full"
      >
        <ul class="m-0 grid list-none gap-4 p-0">
          <li class="m-0 leading-tight">
            <a
              href="/account?view=order-history"
              class="holiday-text-primary !font-galano text-base font-medium leading-tight text-[#403940] hover:underline"
              >Track My Order</a
            >
          </li>
          <li class="m-0 leading-tight">
            <a
              href="https://www.facebook.com/groups/littlesleepies/"
              class="holiday-text-primary !font-galano text-base font-medium leading-tight text-[#403940] hover:underline"
              >Join the VIP Group</a
            >
          </li>
          <li class="m-0 leading-tight">
            <a
              href="/pages/sizing-guide"
              class="holiday-text-primary !font-galano text-base font-medium leading-tight text-[#403940] hover:underline"
              >What size should I buy?</a
            >
          </li>
        </ul>
      </nav>
    </div>
  `
}

function renderNewBillboards() {
  const billboardJson = document.querySelector('[data-billboards-json]')
  if (!billboardJson) return
  const billboardJsonData = JSON.parse(billboardJson.textContent)
  let ugcBillboards = []
  let ugcBillboardPositions = []
  let benefitsBillboards = []
  let benefitsBillboardPositions = []
  if (!billboardJsonData) {
    return
  }

  ugcBillboards = billboardJsonData.ugc_billboards
  ugcBillboardPositions = billboardJsonData.ugc_billboard_positions
  benefitsBillboards = billboardJsonData.benefits_billboards
  benefitsBillboardPositions = billboardJsonData.benefits_billboard_positions
  if (ugcBillboardPositions?.length > 0) {
    renderUGCBillboards(ugcBillboards, ugcBillboardPositions)
  }
  if (benefitsBillboardPositions?.length > 0) {
    renderBenefitsBillboards(benefitsBillboards, benefitsBillboardPositions)
  }
}

function renderBenefitsBillboards(benefitsBillboards, benefitsBillboardPositions) {
  benefitsBillboardPositions.forEach((position, index) => {
    const productGrid = document.querySelector('.ais-Hits-list')
    const itemsPerPage = 48
    const positionPage = Math.floor(position / itemsPerPage) + 1
    const urlParams = new URLSearchParams(window.location.search)
    const currentPage = parseInt(urlParams.get('page')) || 1
    if (positionPage === currentPage && productGrid) {
      const benefitsBillboard = createBenefitsBillboard(benefitsBillboards[index])
      productGrid.insertBefore(benefitsBillboard, productGrid.children[position])
    }
  })
}

function createBenefitsBillboard(benefitsBillboard) {
  const { background_image, background_color, font_color, header, bullet_icon, bullet_items, text_content_position } =
    benefitsBillboard

  let textContentPosition = ''
  let backgroundColorPosition = ''
  switch (text_content_position) {
    case 'Top Left':
      textContentPosition = 'left-4 top-4'
      break
    case 'Center Left':
      textContentPosition = 'left-4 top-1/2 -translate-y-1/2'
      break
    case 'Bottom Left':
      textContentPosition = 'left-4 bottom-4'
      break
    case 'Top Center':
      textContentPosition = 'left-1/2 top-4 -translate-x-1/2'
      break
    case 'Center Center':
      textContentPosition = 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
      break
    case 'Bottom Center':
      textContentPosition = 'left-1/2 bottom-4 -translate-x-1/2'
      break
    case 'Top Right':
      textContentPosition = 'right-4 top-4'
      break
    case 'Center Right':
      textContentPosition = 'right-4 top-1/2 -translate-y-1/2'
      break
    case 'Bottom Right':
      textContentPosition = 'right-4 bottom-4'
      break
    default:
      textContentPosition = 'left-4 top-1/2 -translate-y-1/2'
      break
  }

  switch (text_content_position) {
    case 'Top Left':
      backgroundColorPosition = 'top-0 left-0 bottom-0 right-auto'
      break
    case 'Center Left':
      backgroundColorPosition = 'top-0 left-0 bottom-0 right-auto'
      break
    case 'Bottom Left':
      backgroundColorPosition = 'top-0 left-0 bottom-0 right-auto'
      break
    case 'Top Right':
      backgroundColorPosition = 'top-0 right-0 bottom-0 left-auto'
      break
    case 'Center Right':
      backgroundColorPosition = 'top-0 right-0 bottom-0 left-auto'
      break
    case 'Bottom Right':
      backgroundColorPosition = 'top-0 right-0 bottom-0 left-auto'
      break
    default:
      backgroundColorPosition = 'hidden'
      break
  }

  const svgClass =
    text_content_position === 'Top Right' ||
    text_content_position === 'Center Right' ||
    text_content_position === 'Bottom Right'
      ? 'left-[1px] -translate-x-full scale-x-[-1]'
      : 'right-[1px] translate-x-full'

  const strokeColor = font_color || '#403940'

  const bulletItemsHtml =
    bullet_items && bullet_items.length > 0
      ? `<ul class="list-none m-0 space-y-1.5">
        ${bullet_items
          .map(
            (item) => `
          <li class="flex items-center gap-1" style="color: ${font_color || ''};">
            <svg class="w-5 h-5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 7L8.125 13.875L5 10.75" stroke="${strokeColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="text-xs leading-[125%] font-medium xl:text-base">${item}</span>
          </li>
        `
          )
          .join('')}
      </ul>`
      : ''

  const temp = document.createElement('div')
  temp.innerHTML = `
    <li class="col-span-2 relative w-full aspect-[8/5] rounded-lg overflow-hidden" data-collection-benefits-billboard>
      ${
        background_image
          ? `<img src="${background_image}" alt="" class="w-full h-full object-cover object-center" />`
          : ''
      }
      ${
        background_color
          ? `
        <div class="absolute z-[0] w-[40%] h-full ${backgroundColorPosition}" style="background-color: ${background_color};">
          <svg class="h-full absolute z-[0] top-0 bottom-0 ${svgClass}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 220" fill="none">
            <g clip-path="url(#clip0_2197_11819)">
              <path d="M-9.52016 288.171C-30.0292 435.731 -18.0822 603.067 -9.52016 668.302H-89.7642L-89.7642 -2046.93H4.05961C-9.00245 -1992.62 -47.2329 -1715.48 -9.52016 -1527.65C28.1926 -1339.83 38.2679 -1121.48 -9.52016 -885.064C-47.7506 -695.893 -25.4495 -485.694 -9.52016 -404.263C51.8476 -35.4064 16.1261 103.742 -9.52016 288.081V288.171Z" fill="${background_color}"/>
            </g>
            <defs>
              <clipPath id="clip0_2197_11819">
                <rect width="220" height="41.5094" fill="white" transform="matrix(0 1 -1 0 41.5098 0)"/>
              </clipPath>
            </defs>
          </svg>
        </div>
      `
          : ''
      }
      <div class="absolute z-[1] max-w-[40%] ${textContentPosition}">
        ${
          header
            ? `<h2 class="text-base font-medium leading-[125%] mb-3 xl:text-4xl" style="color: ${
                font_color || ''
              };">${header}</h2>`
            : ''
        }
        ${bulletItemsHtml}
      </div>
    </li>
  `

  return temp.firstElementChild
}

function removeBenefitsBillboards() {
  const benefitsBillboards = document.querySelectorAll('[data-collection-benefits-billboard]')
  benefitsBillboards.forEach((benefitsBillboard) => {
    benefitsBillboard.remove()
  })
}

function renderUGCBillboards(ugcBillboards, ugcBillboardPositions) {
  ugcBillboardPositions.forEach((position, index) => {
    const productGrid = document.querySelector('.ais-Hits-list')
    const itemsPerPage = 48
    const positionPage = Math.floor(position / itemsPerPage) + 1
    const urlParams = new URLSearchParams(window.location.search)
    const currentPage = parseInt(urlParams.get('page')) || 1
    if (positionPage === currentPage && productGrid) {
      const ugcBillboard = createUGCBillboard(ugcBillboards[index])
      productGrid.insertBefore(ugcBillboard, productGrid.children[position])
    }
  })
}

function removeUGCBillboards() {
  const ugcBillboards = document.querySelectorAll('[data-collection-ugc-billboard]')
  ugcBillboards.forEach((ugcBillboard) => {
    ugcBillboard.remove()
  })
}

function createUGCBillboard(ugcBillboard) {
  const {
    background_color,
    badge,
    badge_alt_text,
    video,
    video_alt_text,
    image,
    image_alt_text,
    font_color,
    quote,
    author,
    reviewer_name,
    button_bg_color,
    poster,
  } = ugcBillboard

  // Get badge URL if badge exists (assuming it might be a URL or ID)
  const badgeUrl = badge?.url || badge
  const imageUrl = image?.url || image
  const videoUrl = video?.url || video
  const posterUrl = poster?.url || poster
  const buttonColor = button_bg_color || 'white'
  const authorName = author || reviewer_name

  const temp = document.createElement('div')
  temp.innerHTML = `
    <li
      class="relative rounded-lg !px-5 !pb-5 !pt-10 col-span-2 md:col-span-1"
      style="background-color: ${background_color || ''};"
      data-collection-ugc-billboard
    >
      ${
        badgeUrl
          ? `
        <img
          src="${badgeUrl}"
          alt="${badge_alt_text || ''}"
          class="w-1/4 h-auto absolute z-[3] right-2.5 top-2.5 object-cover object-center"
        />
      `
          : ''
      }
      <div class="group on w-2/3 !mx-auto relative aspect-[4/5] !mb-5 rotate-[-3deg] md:w-4/5" data-video-container data-component="video-player">
        ${
          videoUrl
            ? `
          <button
            class="absolute z-[2] flex h-5 w-5 items-center justify-center rounded-full bottom-4 left-4"
            data-hook="play-pause"
          >
            <span class="sr-only">Play/Pause Video</span>
            <svg
              class="hidden group-on:block"
              viewBox="0 0 27 27"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              data-video-pause-icon
            >
              <path d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z" fill="${buttonColor}"/>
              <rect x="9.75" y="9" width="2.25" height="9" rx="0.75" fill="#3D3C3D"/>
              <rect x="15" y="9" width="2.25" height="9" rx="0.75" fill="#3D3C3D"/>
            </svg>
            <svg
              class="block group-on:hidden"
              viewBox="0 0 27 27"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              data-video-play-icon
            >
              <path d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z" fill="${buttonColor}"/>
              <path d="M18.0144 12.8522C18.5106 13.1416 18.5106 13.8584 18.0144 14.1478L11.2529 18.0921C10.7529 18.3837 10.125 18.0231 10.125 17.4442L10.125 9.55578C10.125 8.97693 10.7529 8.61628 11.2529 8.90794L18.0144 12.8522Z" fill="#3D3C3D"/>
            </svg>
          </button>
          <video
            data-video-player="true"
            data-playing="true"
            autoplay
            loop
            muted
            playsinline
            ${posterUrl ? `poster="${posterUrl}"` : ''}
            preload="metadata"
            class="relative z-[1] w-full h-full object-cover object-center rounded-[4px]"
            src="${videoUrl}"
          >
            ${video_alt_text ? `<p class="sr-only">${video_alt_text}</p>` : ''}
          </video>
        `
            : imageUrl
            ? `
          <img
            src="${imageUrl}"
            alt="${image_alt_text || ''}"
            class="relative z-[1] w-full h-full object-cover object-center rounded-[4px]"
          />
        `
            : ''
        }
        <div class="absolute -left-2 -bottom-2 right-2 top-2 z-[0] w-full h-full bg-pearl rounded-[4px]"></div>
      </div>
      <svg class="block mx-auto mb-3" width="113" height="20" viewBox="0 0 113 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.7915 6.99645C13.7835 7.00428 12.7836 7.21921 11.7759 7.29027C11.5127 7.30861 11.0807 7.1725 10.9883 6.99143C10.3227 5.68047 9.70118 4.3475 9.11165 3.00006C8.87377 2.4598 8.78017 1.81932 7.9772 1.81573C7.20454 1.81158 6.97541 2.41119 6.75972 2.95279C6.28237 4.15198 5.81413 5.35465 5.38114 6.56838C5.22218 7.01026 4.99736 7.11854 4.50531 7.08331C3.43572 7.0093 2.36127 7.04028 1.12885 7.02298C0.789276 7.35449 0.219866 7.66608 0.121569 8.08017C0.0379556 8.43917 0.387821 9.0004 0.718053 9.31857C1.46747 10.0472 2.31426 10.682 3.13192 11.3468C3.62251 11.7458 3.76827 12.1415 3.54895 12.791C3.15762 13.9502 2.95369 15.1642 2.64319 16.3519C2.50177 16.8976 2.57326 17.4325 3.14005 17.5983C3.51487 17.7077 4.08545 17.569 4.42595 17.3471C5.55889 16.6027 6.65095 15.7892 7.71651 14.9583C8.18066 14.5967 8.47704 14.5668 8.95186 14.9646C10.0062 15.8536 11.0989 16.7159 12.2498 17.4913C12.6363 17.7533 13.2489 17.7132 13.7582 17.8157C13.8268 17.332 14.037 16.8223 13.9362 16.3709C13.6257 14.9796 13.208 13.6056 12.8128 12.2305C12.6579 11.688 12.7843 11.3041 13.2618 10.9379C14.0919 10.2889 14.9134 9.61965 15.6339 8.86559C15.9369 8.54681 16.2118 7.91129 16.0712 7.59038C15.9319 7.27238 15.2475 7.0009 14.8018 7.00284L14.8085 7.00052L14.7915 6.99645Z" fill="#FAD588"/>
        <path d="M38.7915 6.99645C37.7835 7.00428 36.7836 7.21921 35.7759 7.29027C35.5127 7.30861 35.0807 7.1725 34.9883 6.99143C34.3227 5.68047 33.7012 4.3475 33.1116 3.00006C32.8738 2.4598 32.7802 1.81932 31.9772 1.81573C31.2045 1.81158 30.9754 2.41119 30.7597 2.95279C30.2824 4.15198 29.8141 5.35465 29.3811 6.56838C29.2222 7.01026 28.9974 7.11854 28.5053 7.08331C27.4357 7.0093 26.3613 7.04028 25.1288 7.02298C24.7893 7.35449 24.2199 7.66608 24.1216 8.08017C24.038 8.43917 24.3878 9.0004 24.7181 9.31857C25.4675 10.0472 26.3143 10.682 27.1319 11.3468C27.6225 11.7458 27.7683 12.1415 27.549 12.791C27.1576 13.9502 26.9537 15.1642 26.6432 16.3519C26.5018 16.8976 26.5733 17.4325 27.1401 17.5983C27.5149 17.7077 28.0854 17.569 28.4259 17.3471C29.5589 16.6027 30.651 15.7892 31.7165 14.9583C32.1807 14.5967 32.477 14.5668 32.9519 14.9646C34.0062 15.8536 35.0989 16.7159 36.2498 17.4913C36.6363 17.7533 37.2489 17.7132 37.7582 17.8157C37.8268 17.332 38.037 16.8223 37.9362 16.3709C37.6257 14.9796 37.208 13.6056 36.8128 12.2305C36.6579 11.688 36.7843 11.3041 37.2618 10.9379C38.0919 10.2889 38.9134 9.61965 39.6339 8.86559C39.9369 8.54681 40.2118 7.91129 40.0712 7.59038C39.9319 7.27238 39.2475 7.0009 38.8018 7.00284L38.8085 7.00052L38.7915 6.99645Z" fill="#FAD588"/>
        <path d="M62.7915 6.99645C61.7835 7.00428 60.7836 7.21921 59.7759 7.29027C59.5127 7.30861 59.0807 7.1725 58.9883 6.99143C58.3227 5.68047 57.7012 4.3475 57.1116 3.00006C56.8738 2.4598 56.7802 1.81932 55.9772 1.81573C55.2045 1.81158 54.9754 2.41119 54.7597 2.95279C54.2824 4.15198 53.8141 5.35465 53.3811 6.56838C53.2222 7.01026 52.9974 7.11854 52.5053 7.08331C51.4357 7.0093 50.3613 7.04028 49.1288 7.02298C48.7893 7.35449 48.2199 7.66608 48.1216 8.08017C48.038 8.43917 48.3878 9.0004 48.7181 9.31857C49.4675 10.0472 50.3143 10.682 51.1319 11.3468C51.6225 11.7458 51.7683 12.1415 51.549 12.791C51.1576 13.9502 50.9537 15.1642 50.6432 16.3519C50.5018 16.8976 50.5733 17.4325 51.1401 17.5983C51.5149 17.7077 52.0854 17.569 52.4259 17.3471C53.5589 16.6027 54.651 15.7892 55.7165 14.9583C56.1807 14.5967 56.477 14.5668 56.9519 14.9646C58.0062 15.8536 59.0989 16.7159 60.2498 17.4913C60.6363 17.7533 61.2489 17.7132 61.7582 17.8157C61.8268 17.332 62.037 16.8223 61.9362 16.3709C61.6257 14.9796 61.208 13.6056 60.8128 12.2305C60.6579 11.688 60.7843 11.3041 61.2618 10.9379C62.0919 10.2889 62.9134 9.61965 63.6339 8.86559C63.9369 8.54681 64.2118 7.91129 64.0712 7.59038C63.9319 7.27238 63.2475 7.0009 62.8018 7.00284L62.8085 7.00052L62.7915 6.99645Z" fill="#FAD588"/>
        <path d="M86.7915 6.99645C85.7835 7.00428 84.7836 7.21921 83.7759 7.29027C83.5127 7.30861 83.0807 7.1725 82.9883 6.99143C82.3227 5.68047 81.7012 4.3475 81.1116 3.00006C80.8738 2.4598 80.7802 1.81932 79.9772 1.81573C79.2045 1.81158 78.9754 2.41119 78.7597 2.95279C78.2824 4.15198 77.8141 5.35465 77.3811 6.56838C77.2222 7.01026 76.9974 7.11854 76.5053 7.08331C75.4357 7.0093 74.3613 7.04028 73.1288 7.02298C72.7893 7.35449 72.2199 7.66608 72.1216 8.08017C72.038 8.43917 72.3878 9.0004 72.7181 9.31856C73.4675 10.0472 74.3143 10.682 75.1319 11.3468C75.6225 11.7458 75.7683 12.1415 75.549 12.791C75.1576 13.9502 74.9537 15.1642 74.6432 16.3519C74.5018 16.8976 74.5733 17.4325 75.1401 17.5983C75.5149 17.7077 76.0854 17.569 76.4259 17.3471C77.5589 16.6027 78.651 15.7892 79.7165 14.9583C80.1807 14.5967 80.477 14.5668 80.9519 14.9646C82.0062 15.8536 83.0989 16.7159 84.2498 17.4913C84.6363 17.7533 85.2489 17.7132 85.7582 17.8157C85.8268 17.332 86.037 16.8223 85.9362 16.3709C85.6257 14.9796 85.208 13.6056 84.8128 12.2305C84.6579 11.688 84.7843 11.3041 85.2618 10.9379C86.0919 10.2889 86.9134 9.61965 87.6339 8.86559C87.9369 8.54681 88.2118 7.91129 88.0712 7.59038C87.9319 7.27238 87.2475 7.0009 86.8018 7.00284L86.8085 7.00052L86.7915 6.99645Z" fill="#FAD588"/>
        <path d="M110.791 6.99645C109.783 7.00428 108.784 7.21921 107.776 7.29027C107.513 7.30861 107.081 7.1725 106.988 6.99143C106.323 5.68047 105.701 4.3475 105.112 3.00006C104.874 2.4598 104.78 1.81932 103.977 1.81573C103.205 1.81158 102.975 2.41119 102.76 2.95279C102.282 4.15198 101.814 5.35465 101.381 6.56838C101.222 7.01026 100.997 7.11854 100.505 7.08331C99.4357 7.0093 98.3613 7.04028 97.1288 7.02298C96.7893 7.35449 96.2199 7.66608 96.1216 8.08017C96.038 8.43917 96.3878 9.0004 96.7181 9.31856C97.4675 10.0472 98.3143 10.682 99.1319 11.3468C99.6225 11.7458 99.7683 12.1415 99.549 12.791C99.1576 13.9502 98.9537 15.1642 98.6432 16.3519C98.5018 16.8976 98.5733 17.4325 99.1401 17.5983C99.5149 17.7077 100.085 17.569 100.426 17.3471C101.559 16.6027 102.651 15.7892 103.717 14.9583C104.181 14.5967 104.477 14.5668 104.952 14.9646C106.006 15.8536 107.099 16.7159 108.25 17.4913C108.636 17.7533 109.249 17.7132 109.758 17.8157C109.827 17.332 110.037 16.8223 109.936 16.3709C109.626 14.9796 109.208 13.6056 108.813 12.2305C108.658 11.688 108.784 11.3041 109.262 10.9379C110.092 10.2889 110.913 9.61965 111.634 8.86559C111.937 8.54681 112.212 7.91129 112.071 7.59038C111.932 7.27238 111.248 7.0009 110.802 7.00284L110.808 7.00052L110.791 6.99645Z" fill="#FAD588"/>
      </svg>
      ${
        quote
          ? `
        <div
          class="text-center font-galano !text-sm !leading-[150%] !mb-2"
          style="color: ${font_color || ''};"
        >
          ${quote}
        </div>
      `
          : ''
      }
      ${
        authorName
          ? `
        <div
          class="text-center font-galano font-medium !text-sm !leading-[125%]"
          style="color: ${font_color || ''};"
        >
          ${authorName}
        </div>
      `
          : ''
      }
    </li>
  `
  return temp.firstElementChild
}

function renderBillboards() {
  const billboardJson = document.querySelector('[data-billboards-json]')
  if (!billboardJson) return

  const billboardJsonData = JSON.parse(billboardJson.textContent)
  const billboards = billboardJsonData.billboards
  const video = billboardJsonData.video
  const positions = billboardJsonData.positions

  if (!!video) {
    const createVideoBillboard = (videoData) => {
      // Default values
      const defaults = {
        video_opacity: 0.9,
        video_dimensions: '16:9',
        ui_color: '#3D3C3D',
        ui_alternate_color: '#fff',
      }

      // Extract data with fallbacks
      const videoUrl = videoData?.video_url || ''
      const text = videoData?.text || ''
      const link = videoData?.link || null
      const videoOpacity = videoData?.video_opacity || defaults.video_opacity
      const videoDimensions = videoData?.video_dimensions || defaults.video_dimensions
      const desktopSpanTwoCols = videoData?.desktop_span_two_cols || false
      const accentImage = videoData?.accent_image || null

      // Set UI colors based on theme
      let uiColor = defaults.ui_color
      let uiAlternateColor = defaults.ui_alternate_color

      if (videoData?.ui_color === 'White') {
        uiColor = '#fff'
        uiAlternateColor = '#3D3C3D'
      }

      // Set aspect ratio
      let aspectRatio = '16/9'
      switch (videoDimensions) {
        case '2:3':
          aspectRatio = '2/3'
          break
        case '1:1':
          aspectRatio = '1/1'
          break
      }

      // Create a temporary container
      const temp = document.createElement('div')

      // Create the entire structure at once using template literal
      temp.innerHTML = `
        <li
          data-component="collection-video-player"
          data-collection-video
          class="relative col-span-2 row-span-2 m-0 rounded-lg ${
            desktopSpanTwoCols ? 'lg:row-span-1' : 'lg:col-span-3'
          }"
          style="--ui-color: ${uiColor}; --video-opacity: ${videoOpacity}; --aspect-ratio: ${aspectRatio}; --ui-alternate-color: ${uiAlternateColor};"
        >
          ${
            accentImage?.value?.url
              ? `
            <img
              src="${accentImage.value.url}"
              alt="${accentImage.value.alt || ''}"
              class="w-12 lg:w-24 h-auto absolute -top-2 -right-2 lg:-top-4 lg:-right-4 z-10"
            />
          `
              : ''
          }
          <collection-video-player class="relative col-span-2 grid h-full grid-cols-1 grid-rows-1 overflow-clip rounded-lg lg:col-span-3">
            <div class="relative col-start-1 row-start-1 overflow-clip rounded-lg opacity-[var(--video-opacity)]">
              ${
                videoUrl
                  ? `
                <video
                  autoplay
                  loop
                  muted
                  playsinline
                  preload="metadata"
                  class="object-cover object-center w-full h-full rounded-lg aspect-[var(--aspect-ratio)]"
                  src="${videoUrl}"
                ></video>
              `
                  : ''
              }
            </div>
            <div class="relative col-start-1 row-start-1 flex items-end justify-between gap-4 overflow-visible !p-4 !!text-[var(--ui-color)] lg:!p-6">
              <div>
                ${
                  text && !link
                    ? `
                  <p class="mb-0 font-galano text-base font-medium leading-normal !text-[var(--ui-color)]">
                    ${text}
                  </p>
                `
                    : ''
                }
                ${
                  link?.url
                    ? `
                  <a
                    href="${link.url}"
                    class="mb-0 font-galano text-base font-medium leading-normal !text-[var(--ui-color)] underline-offset-4 hover:underline flex items-center justify-center"
                  >
                    ${link.text || ''}

                    <svg
                      class="w-3 h-auto ml-1.5"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="12"
                      viewBox="0 0 14 12"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path d="M0.777779 5.18522C0.348223 5.18522 0 5.55001 0 6C0 6.44998 0.348223 6.81478 0.777779 6.81478V5.18522ZM13.7722 6.57613C14.0759 6.25795 14.0759 5.74205 13.7722 5.42386L8.82246 0.238644C8.51873 -0.0795479 8.02626 -0.0795479 7.72252 0.238644C7.41878 0.556831 7.41878 1.07273 7.72252 1.39091L12.1223 6L7.72252 10.609C7.41878 10.9272 7.41878 11.4432 7.72252 11.7614C8.02626 12.0795 8.51873 12.0795 8.82246 11.7614L13.7722 6.57613ZM0.777779 6.81478H13.2222V5.18522H0.777779V6.81478Z" fill="currentColor"/>
                    </svg>
                  </a>
                `
                    : ''
                }
              </div>
              <button
                data-is-playing="true"
                class="group h-7 w-7 overflow-visible p-0 text-galaxy"
              >
                <div class="h-7 w-7 overflow-hidden rounded-full group-data-[is-playing=true]:hidden">
                  <span class="sr-only">play video</span>
                  <svg
                    class="w-full h-full"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-video-play-icon
                  >
                    <path d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z" fill="white"/>
                    <circle cx="13.5" cy="13.5" r="9.75" stroke="#3D3C3D" stroke-width="1.5"/>
                    <path d="M18.0144 12.8522C18.5106 13.1416 18.5106 13.8584 18.0144 14.1478L11.2529 18.0921C10.7529 18.3837 10.125 18.0231 10.125 17.4442L10.125 9.55578C10.125 8.97693 10.7529 8.61628 11.2529 8.90794L18.0144 12.8522Z" fill="#3D3C3D"/>
                  </svg>                  
                </div>
                <div class="h-7 w-7 overflow-hidden rounded-full group-data-[is-playing=false]:hidden">
                  <span class="sr-only">Pause video</span>
                  <svg
                    class="w-full h-full"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-video-pause-icon
                  >
                    <path d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z" fill="white"/>
                    <circle cx="13.5" cy="13.5" r="9.75" stroke="#3D3C3D" stroke-width="1.5"/>
                    <rect x="9.75" y="9" width="2.25" height="9" rx="0.75" fill="#3D3C3D"/>
                    <rect x="15" y="9" width="2.25" height="9" rx="0.75" fill="#3D3C3D"/>
                  </svg>                  
                </div>
              </button>
            </div>
          </collection-video-player>
        </li>
      `.trim()

      // Return the first child (the li element)
      return temp.firstElementChild
    }

    const videoBillboard = createVideoBillboard(video)
    const productGrid = document.querySelector('.ais-Hits-list')
    const position = video.position - 1
    const urlParams = new URLSearchParams(window.location.search)
    const currentPage = parseInt(urlParams.get('page')) || 1
    const itemsPerPage = 48
    const positionPage = Math.floor(position / itemsPerPage) + 1
    if (positionPage === currentPage && productGrid) {
      const productItems = productGrid.querySelectorAll('.ais-Hits-item')
      const productItem = productItems[position % itemsPerPage]
      if (productItem) {
        productItem.parentNode.insertBefore(videoBillboard, productItem)
      }
    }
  }

  if (!!billboards) {
    // Process billboards in reverse order to prevent cumulative offset issues
    // This ensures that later billboards don't affect the positions of earlier ones
    const sortedBillboards = [...billboards].sort((a, b) => {
      const posA = positions[billboards.indexOf(a)] - 1
      const posB = positions[billboards.indexOf(b)] - 1
      return posB - posA // Sort in descending order (highest position first)
    })

    sortedBillboards.forEach((billboard) => {
      const index = billboards.indexOf(billboard)
      const backgroundColor = billboard.background_color || '#A9B8D3'
      // Extract MediaImage IDs and construct CDN URLs
      const mobileImageUrl = billboard.mobile_image_url
      const mobileImageAltText = billboard.mobile_image_alt_text
      const desktopImageUrl = billboard.desktop_image_url
      const desktopImageAltText = billboard.desktop_image_alt_text

      const imageOpacity = parseFloat(billboard.image_opacity) || 1
      const mobileSpanTwoCols = billboard.mobile_span_two_cols
      const desktopColSpan = billboard.desktop_column_span || 1
      const titleColor = billboard.title_color || '#3D3C3D'
      const descColor = billboard.description_color || '#3D3C3D'
      const linkColor = billboard.link_color || '#3D3C3D'
      const contentVerticalAlignment = billboard.content_vertical_alignment || 'Center'
      const titleTopMargin = billboard.title_top_margin
      const titleBottomMargin = billboard.title_bottom_margin
      const descTopMargin = billboard.description_top_margin
      const descBottomMargin = billboard.description_bottom_margin
      const btnLinkTopMargin = billboard.button_link_top_margin
      const btnLinkBottomMargin = billboard.button_link_bottom_margin
      const openInNewTab = billboard.open_in_new_tab
      const buttonBgColor = billboard.button_background_color
      const buttonTextColor = billboard.button_text_color

      let btnStyles, uiColor, uiAlternateColor
      if (buttonBgColor && buttonTextColor) {
        switch (billboard.ui_button_style) {
          case 'Outlined':
            btnStyles =
              '!!text-[var(--button-text-color)] !bg-transparent !border-[var(--button-bg-color)] !transition-colors hover:!border-galaxy hover:!text-white hover:!bg-galaxy !transition-colors hover:!no-underline active:!no-underline'
            break
          case 'Filled':
            btnStyles =
              '!text-[var(--button-text-color)] !bg-[var(--button-bg-color)] !border-[var(--button-bg-color)] hover:!border-galaxy hover:!text-white hover:!bg-galaxy !transition-colors hover:!no-underline active:!no-underline'
            break
        }
      } else {
        switch (billboard.ui_color) {
          case 'White':
            uiColor = '#fff'
            uiAlternateColor = '#3D3C3D'
            break
          case 'Black':
            uiColor = '#3D3C3D'
            uiAlternateColor = '#fff'
            break
        }
        switch (billboard.ui_button_style) {
          case 'Outlined':
            btnStyles =
              '!!text-[var(--ui-color)] !bg-transparent !border-[var(--ui-color)] !transition-colors hover:!border-[var(--ui-alternate-color)] hover:!text-[var(--ui-color)] hover:!bg-[var(--ui-alternate-color)] !transition-colors hover:!no-underline active:!no-underline'
            break
          case 'Filled':
            btnStyles =
              '!text-[var(--ui-alternate-color)] !bg-[var(--ui-color)] !border-[var(--ui-color)] !transition-colors hover:!border-[var(--ui-alternate-color)] hover:!bg-[var(--ui-alternate-color)] hover:!text-[var(--ui-color)] !transition-colors hover:!no-underline active:!no-underline'
            break
        }
      }

      const colSpanClassesMobile = mobileSpanTwoCols
        ? 'col-span-2 row-span-2 w-full h-[496px] lg:h-full lg:min-h-[355px]'
        : 'w-full h-full'

      let colSpanClassesDesktop
      switch (desktopColSpan) {
        case 1:
          colSpanClassesDesktop = 'lg:col-span-1 lg:row-span-1'
          break
        case 2:
          colSpanClassesDesktop = 'lg:col-span-2 lg:row-span-1'
          break
        case 3:
          colSpanClassesDesktop = 'lg:col-span-3 lg:row-span-2 lg:aspect-16/9'
          break
      }

      let contentAlignmentClass
      switch (contentVerticalAlignment) {
        case 'Top':
          contentAlignmentClass = 'justify-start'
          break
        case 'Center':
          contentAlignmentClass = 'justify-center'
          break
        case 'Bottom':
          contentAlignmentClass = 'justify-end'
          break
        case 'Between':
          contentAlignmentClass = 'justify-between'
          break
      }

      const titleMarginClass = [titleTopMargin ? '!mt-auto' : '', titleBottomMargin ? '!mb-auto' : '!mb-1.5']
        .filter(Boolean)
        .join(' ')

      const descMarginClass = [descTopMargin ? '!mt-auto' : '', descBottomMargin ? '!mb-auto' : '!mb-4']
        .filter(Boolean)
        .join(' ')

      const btnLinkMarginClass = [btnLinkTopMargin ? '!mt-auto' : '', btnLinkBottomMargin ? '!mb-auto' : '!mb-0']
        .filter(Boolean)
        .join(' ')

      // Create the billboard element
      const billboardElement = document.createElement('li')
      billboardElement.setAttribute('data-collection-billboard', '')
      billboardElement.className = `group !relative !m-0 !overflow-clip !rounded-lg lg:!rounded-2xl !bg-[var(--background-color)] !!text-[var(--ui-color)] ${colSpanClassesMobile} ${colSpanClassesDesktop}`
      billboardElement.style.setProperty('--background-color', backgroundColor)
      billboardElement.style.setProperty('--ui-color', uiColor)
      billboardElement.style.setProperty('--ui-alternate-color', uiAlternateColor)
      billboardElement.style.setProperty('--image-opacity', imageOpacity)
      billboardElement.style.setProperty('--title-color', titleColor)
      billboardElement.style.setProperty('--desc-color', descColor)
      billboardElement.style.setProperty('--link-color', linkColor)
      billboardElement.style.setProperty('--button-bg-color', buttonBgColor)
      billboardElement.style.setProperty('--button-text-color', buttonTextColor)

      // Image container
      const imageContainer = document.createElement('div')
      imageContainer.className =
        '!absolute !left-1/2 !top-1/2 !h-full !w-full !-translate-x-1/2 !-translate-y-1/2 !opacity-[var(--image-opacity)] !transition-transform !duration-500 group-hover:!scale-110'
      if (mobileImageUrl) {
        const img = document.createElement('img')
        img.src = mobileImageUrl
        img.alt = mobileImageAltText
        img.loading = 'lazy'
        img.className = '!object-cover !object-center !h-full !w-full lg:!hidden'
        imageContainer.appendChild(img)
      }
      if (desktopImageUrl) {
        const img = document.createElement('img')
        img.src = desktopImageUrl
        img.alt = desktopImageAltText
        img.loading = 'lazy'
        img.className = '!object-cover !object-center !h-full !w-full lg:!block !hidden'
        imageContainer.appendChild(img)
      }

      // Content container
      const contentContainer = document.createElement('div')
      contentContainer.className = `!relative !flex !h-full !w-full !flex-col !items-center !px-3 !py-6 !text-center !font-galano lg:!px-6 ${contentAlignmentClass}`
      if (billboard.title) {
        const h3 = document.createElement('h3')
        h3.className = `!font-galano !text-3xl !font-medium !leading-tight !text-[var(--title-color)] lg:!text-4xl ${titleMarginClass}`
        h3.textContent = billboard.title
        contentContainer.appendChild(h3)
      }
      if (billboard.description && billboard.description.children) {
        const descriptionText = billboard.description.children
          .map((paragraph) => {
            if (!paragraph.children) return ''
            return paragraph.children
              .map((textNode) => {
                if (textNode.type !== 'text') return ''
                const text = textNode.value || ''
                return textNode.bold ? `<strong>${text}</strong>` : text
              })
              .filter((text) => text)
              .join('')
          })
          .filter((text) => text)
          .join('<br>')
        if (descriptionText) {
          const p = document.createElement('p')
          p.className = `!font-galano !text-base !font-medium !leading-normal !text-[var(--desc-color)] [&_p]:!m-0 [&_p]:!text-[var(--desc-color)] ${descMarginClass}`
          p.innerHTML = descriptionText
          contentContainer.appendChild(p)
        }
      }
      if (billboard.show_button || billboard.show_link) {
        const buttonContainer = document.createElement('div')
        buttonContainer.className = `!flex !flex-col !items-center !gap-3 ${btnLinkMarginClass}`
        if (billboard.show_button) {
          const a = document.createElement('a')
          a.href = billboard.button_url || '#'
          if (openInNewTab) a.target = '_blank'
          buttonContainer.appendChild(a)
          a.textContent = billboard.button_label || ''
          a.className = `!flex !items-center !justify-center !rounded-lg !border-2 !border-solid !px-7 !py-2.5 !font-galano !text-sm !font-semibold !leading-tight ${btnStyles}`
        }

        if (billboard.show_link) {
          const link = document.createElement('a')
          link.href = billboard.link_url || '#'
          if (openInNewTab) link.target = '_blank'
          link.textContent = billboard.link_label || ''
          link.className =
            '!mt-auto !font-galano !text-base !font-medium !leading-tight !text-[var(--link-color)] !underline-offset-4 hover:!text-[var(--link-color)] hover:!underline !flex !items-center !justify-center'
          const arrow = document.createElement('span')
          arrow.setAttribute('aria-hidden', 'true')
          arrow.className = '!w-3 !h-auto !ml-1.5 !flex'
          arrow.innerHTML = `
            <svg class="w-3 h-auto" xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
            <path d="M0.777779 5.18522C0.348223 5.18522 0 5.55001 0 6C0 6.44998 0.348223 6.81478 0.777779 6.81478V5.18522ZM13.7722 6.57613C14.0759 6.25795 14.0759 5.74205 13.7722 5.42386L8.82246 0.238644C8.51873 -0.0795479 8.02626 -0.0795479 7.72252 0.238644C7.41878 0.556831 7.41878 1.07273 7.72252 1.39091L12.1223 6L7.72252 10.609C7.41878 10.9272 7.41878 11.4432 7.72252 11.7614C8.02626 12.0795 8.51873 12.0795 8.82246 11.7614L13.7722 6.57613ZM0.777779 6.81478H13.2222V5.18522H0.777779V6.81478Z" fill="currentColor"></path>
          </svg>
          `
          link.appendChild(arrow)
          buttonContainer.appendChild(link)
        }

        contentContainer.appendChild(buttonContainer)
      }

      // Assemble the final element
      billboardElement.appendChild(imageContainer)
      billboardElement.appendChild(contentContainer)

      const productGrid = document.querySelector('.ais-Hits-list')
      const position = positions[index] - 1

      // Get current page from URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const currentPage = parseInt(urlParams.get('page')) || 1
      const itemsPerPage = 48

      // Calculate the position's page number (1-based)
      const positionPage = Math.floor(position / itemsPerPage) + 1

      // Only show billboard if it belongs on the current page
      if (positionPage === currentPage) {
        // Get the product item at the specified position
        const productItem = productGrid.children.item(position % itemsPerPage)

        if (productItem) {
          // If the product item exists at that position, insert before it
          productItem.parentNode.insertBefore(billboardElement, productItem)
        }
      }
    })
  }
}

function removeBillboards() {
  if (!collectionHandle && !searchPage) return
  document.querySelectorAll('[data-collection-billboard]').forEach((el) => el.remove())
  document.querySelectorAll('[data-collection-video]').forEach((el) => el.remove())
}

function renderPersonalizationBlock() {
  if (document.querySelector('[data-personalization-block]')) return

  const hitsContainer = document.querySelector('.ais-hits-container')
  if (!hitsContainer) return

  const billboardJson = document.querySelector('[data-billboards-json]')
  if (!billboardJson) return

  let data
  try {
    data = JSON.parse(billboardJson.textContent)
  } catch (_e) {
    return
  }

  const blockData = data?.personalization_block
  if (!blockData?.steps?.length) return

  const container = document.createElement('div')
  container.setAttribute('data-personalization-block', '')
  container.className =
    '!mb-10 flex flex-col items-center justify-center gap-4 rounded-2xl bg-[var(--bg-color)] !px-4 !py-5 text-center lg:!mb-8 lg:flex-row lg:items-start lg:gap-10 lg:!px-5 xl:gap-14'
  container.style.cssText = `--bg-color: ${blockData.background_color}; --text-color: ${blockData.text_color}; --step-bg-color: ${blockData.step_background_color}`

  blockData.steps.forEach((step, i) => {
    const stepEl = document.createElement('div')
    stepEl.className = 'flex flex-row-reverse items-center justify-center gap-4 lg:flex-col lg:gap-2'

    const img = document.createElement('img')
    img.src = step.image_url
    img.width = 1000
    img.alt = ''
    img.loading = 'eager'
    img.className = 'w-full max-w-[40%] h-auto lg:min-w-28 lg:max-w-1/2'

    const textWrapper = document.createElement('div')
    textWrapper.className = 'flex flex-col items-center justify-center gap-1 lg:gap-2'

    const stepNum = document.createElement('span')
    stepNum.className =
      'mx-auto !mb-1 flex aspect-square items-center justify-center rounded-full bg-[var(--step-bg-color)] w-7 h-7 font-semibold text-[var(--text-color)] lg:!mb-0 !pt-0.5'
    stepNum.textContent = String(i + 1)

    const title = document.createElement('h3')
    title.className = '!mb-0 text-center !text-base !font-medium !leading-tight text-[var(--text-color)] lg:!text-lg'
    title.textContent = step.title || ''

    const subtitle = document.createElement('p')
    subtitle.className = '!m-0 text-center !text-xs !font-medium !leading-tight text-[var(--text-color)] lg:!text-sm'
    subtitle.textContent = step.subtitle || ''

    textWrapper.append(stepNum, title, subtitle)
    stepEl.append(img, textWrapper)
    container.appendChild(stepEl)
  })

  hitsContainer.insertBefore(container, hitsContainer.firstChild)
}

// ─── Quick-add-to-cart ───────────────────────────────────────────────────────

function _ensureQuickAddDOM() {
  if (document.getElementById('quick-add-panel')) return

  const panel = document.createElement('div')
  panel.id = 'quick-add-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-label', 'Select a size')
  panel.setAttribute('aria-modal', 'true')
  panel.className =
    'fixed z-40 hidden p-4 bg-white font-galano lg:p-3 lg:rounded-b-lg lg:bg-white/80 lg:border-0 lg:border-t lg:border-solid lg:border-white'
  panel.innerHTML = [
    '<div class="flex items-center justify-between pb-3 lg:pb-2">',
    '  <span class="font-galano text-sm font-medium leading-tight text-galaxy">Select a Size</span>',
    '  <button data-quick-add-close type="button" aria-label="Close size selector"',
    '    class="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors">',
    '    <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" aria-hidden="true">',
    '      <path d="M0.875 13.125L13.125 0.875" stroke="#3D3C3D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    '      <path d="M13.125 13.125L0.875001 0.874998" stroke="#3D3C3D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    '    </svg>',
    '  </button>',
    '</div>',
    '<div data-quick-add-sizes class="flex flex-wrap gap-2 lg:gap-1"></div>',
    '<div data-quick-add-feedback class="hidden text-center font-galano text-sm font-medium leading-tight"></div>',
  ].join('')
  document.body.appendChild(panel)

  const overlay = document.createElement('div')
  overlay.id = 'quick-add-overlay'
  overlay.className = 'fixed inset-0 z-50 hidden bg-galaxy/40'
  document.body.appendChild(overlay)

  overlay.addEventListener('click', _closeQuickAdd)
  panel.querySelector('[data-quick-add-close]').addEventListener('click', _closeQuickAdd)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') _closeQuickAdd()
  })
  window.addEventListener(
    'scroll',
    function () {
      if (!isMobile) _closeQuickAdd()
    },
    { passive: true }
  )
  window.addEventListener('resize', _closeQuickAdd, { passive: true })
}

function _closeQuickAdd() {
  const panel = document.getElementById('quick-add-panel')
  const overlay = document.getElementById('quick-add-overlay')
  if (panel) {
    if (panel._quickAddTriggerBtn) {
      panel._quickAddTriggerBtn.classList.remove('lg:!hidden')
      panel._quickAddTriggerBtn = null
    }
    if (panel._quickAddHoverImg) {
      panel._quickAddHoverImg.classList.remove('!opacity-100')
      panel._quickAddHoverImg = null
    }
    panel.classList.add('hidden')
  }
  if (overlay) overlay.classList.add('hidden')
}

function _positionQuickAddPanel(triggerEl) {
  const panel = document.getElementById('quick-add-panel')
  if (!panel) return
  // For algolia cards triggerEl is the button inside [data-quick-add-image].
  // For Liquid cards it is the <quick-add-card> element (sibling of [data-product-image]).
  let imageEl = triggerEl.closest('[data-quick-add-image]')
  if (!imageEl) {
    const parent = triggerEl.parentElement
    imageEl = (parent && parent.querySelector('[data-product-image]')) || parent || triggerEl
  }
  const rect = imageEl.getBoundingClientRect()
  panel.classList.remove('hidden')
  if (isMobile) {
    panel.style.cssText = 'bottom:0;left:0;right:0;width:100%;'
  } else {
    let left = Math.round(rect.left)
    const width = Math.round(rect.width)
    if (left + width > window.innerWidth) left = window.innerWidth - width
    const bottom = Math.round(window.innerHeight - rect.bottom - 1)
    panel.style.cssText = `left:${left}px;bottom:${bottom}px;width:${width}px;`
  }
}

async function _openQuickAdd(handle, triggerEl) {
  _ensureQuickAddDOM()
  if (window.clarity && typeof window.clarity === 'function') window.clarity('event', 'plpOpenQuickAdd')
  const panel = document.getElementById('quick-add-panel')
  const overlay = document.getElementById('quick-add-overlay')

  // Hide the trigger button while the panel is open; restore any previously hidden one
  const triggerBtn = triggerEl.tagName === 'BUTTON' ? triggerEl : triggerEl.querySelector('button')
  if (panel._quickAddTriggerBtn && panel._quickAddTriggerBtn !== triggerBtn) {
    panel._quickAddTriggerBtn.classList.remove('lg:!hidden')
  }
  panel._quickAddTriggerBtn = triggerBtn || null
  if (triggerBtn) triggerBtn.classList.add('lg:!hidden')

  // Pin the hover image visible while the panel is open; restore any previously pinned one
  if (panel._quickAddHoverImg) {
    panel._quickAddHoverImg.classList.remove('!opacity-100')
  }
  const hoverImg =
    triggerEl.closest('[data-quick-add-image]')?.querySelector('[data-hover-image]') ||
    triggerEl.parentElement?.querySelector('[data-hover-image]')
  panel._quickAddHoverImg = hoverImg || null
  if (hoverImg) hoverImg.classList.add('!opacity-100')

  // Replace sizes container to discard previous event listeners
  const oldSizes = panel.querySelector('[data-quick-add-sizes]')
  const sizesContainer = oldSizes.cloneNode(false)
  oldSizes.parentNode.replaceChild(sizesContainer, oldSizes)

  const feedback = panel.querySelector('[data-quick-add-feedback]')
  feedback.className = 'hidden px-4 pb-3 text-center font-galano text-sm font-medium leading-tight'
  feedback.textContent = ''

  _positionQuickAddPanel(triggerEl)
  if (isMobile) overlay.classList.remove('hidden')

  try {
    const root = window.Shopify?.routes?.root || '/'
    const res = await fetch(`${root}products/${handle}.js`)
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
    const product = await res.json()

    const sizeIdx = product.options.findIndex((o) => {
      const name = typeof o === 'string' ? o : String(o.name || '')
      return name.toLowerCase() === 'size'
    })
    const optIdx = sizeIdx >= 0 ? sizeIdx : 0

    const seen = new Set()
    const sizes = []
    for (const v of product.variants) {
      const label = v[`option${optIdx + 1}`]
      if (!seen.has(label)) {
        seen.add(label)
        sizes.push({ id: v.id, label, available: v.available })
      }
    }

    sizesContainer.innerHTML = ''
    for (const { id, label, available } of sizes) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.dataset.quickAddVariant = id
      if (triggerBtn?.hasAttribute('data-quick-add-btn-search')) btn.setAttribute('data-quick-add-variant-search', '')
      if (triggerBtn?.hasAttribute('data-quick-add-btn-plp')) btn.setAttribute('data-quick-add-variant-plp', '')
      const displayLabel = label === 'Default Title' ? 'Default Size' : label
      btn.textContent = displayLabel
      if (available) {
        btn.setAttribute('aria-label', `Select size ${displayLabel}`)
        btn.className =
          'p-1.5 rounded border border-solid border-galaxy bg-white text-galaxy font-galano text-sm font-medium leading-tight cursor-pointer transition-colors hover:lg:border-galaxy hover:lg:bg-galaxy hover:lg:text-white active:bg-galaxy active:text-white active:border-galaxy focus:bg-galaxy focus:text-white focus:border-galaxy'
      } else {
        btn.setAttribute('aria-label', `${displayLabel} \u2013 out of stock`)
        btn.setAttribute('aria-disabled', 'true')
        btn.disabled = true
        btn.className =
          'p-1.5 rounded border border-solid border-mist bg-white text-mist font-galano text-sm font-medium leading-tight cursor-not-allowed pdp-variant-select-label-disabled overflow-hidden relative'
      }
      sizesContainer.appendChild(btn)
    }

    sizesContainer.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add-variant]:not([disabled])')
      if (!btn) return
      const variantId = btn.dataset.quickAddVariant
      const enabledBtns = Array.from(sizesContainer.querySelectorAll('button:not([disabled])'))
      enabledBtns.forEach((b) => {
        b.disabled = true
      })
      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        })
        if (!addRes.ok) {
          const errData = await addRes.json().catch(() => ({}))
          throw new Error(errData.description || 'Cart error')
        }
        const addedItem = await addRes.json().catch(() => ({}))
        _closeQuickAdd()
        document.dispatchEvent(new CustomEvent('atcSuccess', { detail: { items: addedItem } }))
      } catch (_err) {
        feedback.textContent = 'Something went wrong. Please try again.'
        feedback.className = 'block px-4 pb-3 text-center font-galano text-sm font-medium leading-tight text-[#dc2626]'
        enabledBtns.forEach((b) => {
          b.disabled = false
          b.classList.remove('opacity-50')
        })
      }
      if (window.clarity && typeof window.clarity === 'function') window.clarity('event', 'plpQuickAddToCart')
    })
  } catch (_err) {
    console.error('[quick-add] failed to load product:', handle, _err)
    sizesContainer.innerHTML =
      '<span class="block w-full py-1 text-center font-galano text-xs text-[#dc2626]">Could not load sizes. Please try again.</span>'
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function createInstantSearchProductCard(item, html, helpers = algoliaShopify.helpers) {
  const hit = item
  const tag = item.tags?.find((tag) => tag.includes('tag_'))?.replace('tag_', '') || ''
  const productName = item.meta?.custom?.product_name || ''
  const productCategory = item.meta?.custom?.product_category || ''
  const productType = item.product_type || ''
  const promoCodeMetafield = item.meta?.custom?.promo_code || ''
  const promoCode = promoCodes[promoCodeMetafield] || ''
  const fabricIconMetaField = item.meta?.custom?.fabric_icon || ''
  const fabricIcon = fabricIcons[fabricIconMetaField] || ''
  const badgeMetafield = item.meta?.custom?.product_card_badge || ''
  const badge = badges[badgeMetafield] || ''
  const soldOut = item.variants_inventory_count <= 0 && item.handle !== 'digital-gift-card' // digital gift card sold out badge fix
  const onSale = item.compare_at_price > item.price
  const babyProduct = item.meta?.custom?.baby_matching_product != null
  const lowStockBadgeDelay = window.themeSettings?.low_stock_badge_delay || 2
  const delaySeconds = lowStockBadgeDelay * 3600 // convert hours to seconds
  const publishedAt = new Date(item.published_at)
  const publishedAtSeconds = publishedAt.getTime() / 1000 // convert to seconds
  const lowStockBadgeCutoff = publishedAtSeconds + delaySeconds
  const currentTime = new Date().getTime() / 1000 // convert to seconds
  const showLowStockBadge = currentTime >= lowStockBadgeCutoff && publishedAtSeconds !== 0
  const lowStock =
    !soldOut &&
    item.variants_inventory_count > 0 &&
    item.variants_inventory_count <= (window.themeSettings?.low_stock_badge_threshold || 10) &&
    showLowStockBadge
  let badgeBGColor = ''
  let badgeTextColor = '#ffffff'
  let badgeTextColorAnimated = '#ffffff'
  let animateTextColor = false
  let badgeText = ''
  let isLowStockBadge = false
  // Priority order: soldOut > onSale > lowStock > babyProduct
  if (soldOut) {
    badgeBGColor = window.themeSettings?.sold_out_badge_bg_color || '#DDDDDD'
    badgeText = 'Sold Out'
    badgeTextColor = window.themeSettings?.sold_out_badge_text_color || '#3D3C3D'
  } else if (onSale) {
    badgeBGColor = window.themeSettings?.sale_badge_bg_color || '#cd4926'
    badgeText = 'Sale'
    badgeTextColor = window.themeSettings?.sale_badge_text_color || '#ffffff'
  } else if (lowStock) {
    badgeBGColor = window.themeSettings?.low_stock_badge_bg_color || '#FF6B35'
    badgeText = window.themeSettings?.low_stock_badge_text || 'Low Stock'
    badgeTextColor = window.themeSettings?.low_stock_badge_text_color || '#ffffff'
    isLowStockBadge = true
  } else if (babyProduct) {
    badgeBGColor = '#FBD9D1'
    badgeText = 'Baby sizes available!'
    badgeTextColor = '#3D3C3D'
  } else if (badge) {
    badgeBGColor = badge.background_color || '#3D3C3D'
    badgeText = badge.content || ''
    badgeTextColor = badge.text_color || '#ffffff'
    badgeTextColorAnimated = badge.text_color_animated || badgeTextColor
    animateTextColor = badge.animate_text_color || false
  }
  // normalize — Algolia may return boolean true or string "true"
  const isVipEarlyAccess =
    item.meta?.custom?.vip_early_access_product === true || item.meta?.custom?.vip_early_access_product === 'true'
  const hoverImage = item.meta?.algolia?.hover_image || ''
  const videoMeta = item.meta?.algolia?.video
  const compare_at_price = Number(item.compare_at_price)
  const compareAtPrice = compare_at_price !== 0 ? Number(item.compare_at_price) : null
  const pathname = location.pathname
  const isSearchPage = pathname.includes('search')
  const isCollectionPage = /\/collections\//i.test(pathname)
  let collectionHandle

  isSearchPage ? (collectionHandle = '') : (collectionHandle = pathname)

  return html`
    <div
      class="ais-hit ais-product group relative !m-0 min-w-[120px] overflow-hidden rounded-lg !p-0 text-left xl:rounded-2xl"
      data-algolia-index="${item.index}"
      data-algolia-position="${item.productPosition}"
      data-algolia-queryid="${item.queryID}"
      data-algolia-objectid="${item.objectID}"
      data-handle="${item.handle}"
      data-variant-id="${item.objectID}"
      data-distinct="${item._distinct}"
      data-product-id="${item.id}"
      onClick="${() => helpers.handleItemClick(hit)}"
    >
      <div
        data-utility="link-to"
        data-link="${collectionHandle}/products/${item.handle}"
        class="ml-[13579px] block h-full hover:cursor-pointer hover:!no-underline active:!no-underline"
      >
        <div
          class="on group relative h-full overflow-hidden rounded-lg text-left xl:rounded-2xl"
          data-component="video-player"
        >
          ${badgeText !== '' && badgeBGColor !== ''
            ? html`
                <div
                  class="${isLowStockBadge
                    ? 'collection-inventory-badge !absolute !left-1 !top-1 xl:!left-2.5 xl:!top-2.5 z-20 !flex !items-center !gap-1.5 !rounded-full !px-3 !py-1'
                    : 'absolute left-1 top-1 xl:!left-2.5 xl:!top-2.5 z-20 rounded-full !px-3 !py-1'}"
                  style="color: ${badgeTextColor}; background-color: ${badgeBGColor}; --text-color: ${badgeTextColor}; --text-color-animated: ${badgeTextColorAnimated};"
                >
                  ${isLowStockBadge && window.themeSettings?.low_stock_badge_svg_code
                    ? html`<div class="!flex !h-2.5 !w-2.5 !flex-shrink-0 !items-center !justify-center">
                        ${html([window.themeSettings.low_stock_badge_svg_code])}
                      </div>`
                    : ''}
                  <div
                    class="${isLowStockBadge
                      ? '!font-galano !text-xxs !font-medium !leading-tight md:!text-xs'
                      : `!text-xxs !font-medium !leading-tight md:!text-xs [&_p:not(:last-child)]:!mr-1 [&_p]:!inline-block [&_p]:!text-xs [&_p]:!font-medium [&_p]:!leading-tight ${
                          animateTextColor ? 'animate-colorChange' : ''
                        }`}"
                  >
                    ${badgeText}
                  </div>
                </div>
              `
            : ''}
          ${!!videoMeta
            ? html`
                <button
                  class="absolute bottom-4 right-4 z-20 flex h-7 w-7 items-center justify-center overflow-hidden rounded-full p-0"
                  data-hook="play-pause"
                >
                  <span class="sr-only">Play/Pause Video</span>
                  <svg
                    class="hidden h-full w-full group-on:block"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-video-pause-icon
                  >
                    <path
                      d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z"
                      fill="white"
                    />
                    <rect
                      x="9.75"
                      y="9"
                      width="2.25"
                      height="9"
                      rx="0.75"
                      fill="#3D3C3D"
                    />
                    <rect
                      x="15"
                      y="9"
                      width="2.25"
                      height="9"
                      rx="0.75"
                      fill="#3D3C3D"
                    />
                  </svg>
                  <svg
                    class="block h-full w-full group-on:hidden"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-video-play-icon
                  >
                    <path
                      d="M0 13.5C0 6.04416 6.04416 0 13.5 0C20.9558 0 27 6.04416 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04416 27 0 20.9558 0 13.5Z"
                      fill="white"
                    />
                    <path
                      d="M18.0144 12.8522C18.5106 13.1416 18.5106 13.8584 18.0144 14.1478L11.2529 18.0921C10.7529 18.3837 10.125 18.0231 10.125 17.4442L10.125 9.55578C10.125 8.97693 10.7529 8.61628 11.2529 8.90794L18.0144 12.8522Z"
                      fill="#3D3C3D"
                    />
                  </svg>
                </button>
              `
            : ''}
          <div
            class="!aspect-4/5"
            data-quick-add-image
          >
            ${!!videoMeta
              ? html`
                  <video
                    data-video-player="true"
                    class="h-full w-full object-cover object-center"
                    autoplay
                    data-playing="true"
                    data-pdp-video
                    src="${videoMeta}"
                    loop
                    muted
                    playsinline
                    preload="none"
                  ></video>
                `
              : html`
                  <img
                    class="h-full w-full object-cover object-center"
                    src="${resizedImage(helpers.originalImage(hit), 400)}"
                    srcset="${cardImageSrcset(helpers.originalImage(hit))}"
                    sizes="${CARD_IMAGE_SIZES}"
                    decoding="async"
                    alt="${item.title}${item.title.includes('Bundle') && !item.title.includes('Gift Set')
                      ? ' 25% Off Three, Pick 3 Get 25% Off'
                      : ''}"
                  />
                `}
            ${!videoMeta && hoverImage !== '' && hoverImage !== '#N/A' && supportsHover()
              ? html`
                  <img
                    data-hover-image
                    class="pointer-events-none absolute left-0 top-0 z-10 h-full w-full object-cover object-center opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100"
                    src="${resizedImage(hoverImage, 400)}"
                    srcset="${cardImageSrcset(hoverImage)}"
                    sizes="${CARD_IMAGE_SIZES}"
                    decoding="async"
                    loading="lazy"
                    alt="${item.title}"
                  />
                `
              : ''}
            ${!soldOut && !productType.includes('Bundle') && !isVipEarlyAccess
              ? html`
                  <button
                    class="absolute bottom-2 right-2 z-20 flex w-fit cursor-pointer items-center justify-center gap-1 rounded-full border border-solid border-white bg-newbornWhite/70 p-1.5 font-galano text-sm font-semibold text-galaxy opacity-100 transition-all focus-within:opacity-100 focus:opacity-100  focus-visible:opacity-100 lg:bottom-3 lg:right-3 lg:px-3 lg:opacity-0 hover:lg:bg-newbornWhite group-hover:lg:opacity-100"
                    data-quick-add-btn
                    data-quick-add-btn-search=${isSearchPage ? '' : undefined}
                    data-quick-add-btn-plp=${isCollectionPage ? '' : undefined}
                    type="button"
                    aria-label="Quick add ${item.title} to cart"
                    onClick="${(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      _openQuickAdd(item.handle, e.currentTarget)
                    }}"
                  >
                    <svg
                      class="h-3 w-3 shrink-0"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M1 9H17"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                      <path
                        d="M9 17L9 1"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                    <span class="hidden !text-sm !leading-normal lg:inline">Add</span>
                  </button>
                `
              : ''}
          </div>

          ${
            /* fabricIcon !== ''
            ? html`
                <div class="absolute right-1 top-1 z-10 max-w-[30px] xl:right-2.5 xl:top-2.5 xl:max-w-10">
                  <span class="sr-only">${fabricIcon.name} Fabric</span>
                  <img
                    src="${fabricIcon.image}"
                    alt="${fabricIcon.name}"
                    class="h-auto w-full object-cover"
                  />
                </div>
              `
            : '' */ ''
          }
        </div>

        <div class="grid-product__meta !mt-3.5 !p-0 !text-center">
          ${tag
            ? html`
                <div class="!mx-auto !mb-2 w-fit rounded-[100px] border border-solid border-mist !px-2.5 !py-1">
                  <p
                    class="!mb-0 text-center font-galano !text-xs font-medium capitalize !leading-tight !tracking-normal text-[var(--text-color)]"
                  >
                    ${tag}
                  </p>
                </div>
              `
            : ''}
          ${productName
            ? html`
                <a
                  href="${collectionHandle}/products/${item.handle}"
                  class="!no-underline hover:!no-underline group-hover:cursor-pointer"
                >
                  <h3
                    data-cy="search-product-card-title"
                    class="!mb-1 text-center font-galano text-base font-medium leading-tight text-[var(--text-color)] xl:!text-lg"
                  >
                    ${productName}
                  </h3>
                </a>
              `
            : ''}
          ${productCategory
            ? html`
                <h4
                  class="!mb-1.5 text-center !font-galano !text-sm !font-medium !leading-normal text-[var(--text-color)]"
                >
                  ${productCategory}
                </h4>
              `
            : ''}
          <p class="ais-hit--price !m-0 !flex justify-center gap-1 !text-center">
            ${compareAtPrice &&
            compareAtPrice !== 0 &&
            compareAtPrice !== null &&
            html`
              <span
                class="ais-hit--price-striked !my-0 !ml-0 !mr-1.5 !font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)] line-through"
                >${helpers.displayStrikedPrice(item.price, item.compare_at_price)}</span
              >
            `}
            ${item.variants_min_price !== item.variants_max_price
              ? html`
                  <b class="!font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)]"
                    >from ${formatPrice(item.variants_min_price)}</b
                  >
                `
              : html`
                  <b class="!font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)]"
                    >${helpers.displayPrice(hit, item._distinct)}</b
                  >
                `}
          </p>
        </div>
        ${promoCode
          ? html`
              <p
                class="!lg:px-5 !mx-auto !mb-0 !mt-2 !flex !max-w-fit !flex-wrap !items-center !justify-center !gap-1 !rounded-full !border !border-solid !border-[var(--ui-color)] !bg-[var(--ui-bg-color)] !px-4 !py-2 !font-galano !text-xs !font-medium !leading-tight !text-stone"
                style="--ui-color: ${promoCode.color}; --ui-bg-color: ${promoCode.color}33;"
              >
                ${promoCode.text}
                ${promoCode.code
                  ? html`
                      <span class="!flex !items-center !justify-center !gap-1 !text-xs !font-medium !capitalize">
                        <svg
                          class="h-auto w-3.5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 14 15"
                          fill="none"
                        >
                          <path
                            d="M1.08811 8.10667L5.86811 12.8867C5.99194 13.0106 6.13899 13.109 6.30086 13.1761C6.46272 13.2432 6.63622 13.2777 6.81145 13.2777C6.98667 13.2777 7.16017 13.2432 7.32203 13.1761C7.4839 13.109 7.63095 13.0106 7.75478 12.8867L13.0904 7.55728C13.3408 7.30719 13.4814 6.96782 13.4814 6.61393V1.83333C13.4814 1.09695 12.8845 0.5 12.1481 0.5H7.36706C7.01344 0.5 6.6743 0.640476 6.42425 0.890524L1.08811 6.22667C0.839777 6.47648 0.700388 6.81442 0.700388 7.16667C0.700388 7.51891 0.839777 7.85685 1.08811 8.10667Z"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></path>
                          <circle
                            cx="10.8145"
                            cy="3.16406"
                            r="1"
                            fill="currentColor"
                          ></circle>
                        </svg>
                        ${promoCode.code}
                      </span>
                    `
                  : ''}
              </p>
            `
          : ''}
      </div>
    </div>
  `
}
function createAutocompleteProductCard(item, html, helpers = algoliaShopify.helpers, itemLink, trackSearchAttribution) {
  const hit = item
  const tag = item.tags?.find((tag) => tag.includes('tag_'))?.replace('tag_', '') || ''
  const productName = item.meta?.custom?.product_name || ''
  const productCategory = item.meta?.custom?.product_category || ''
  const fabricIconMetaField = item.meta?.custom?.fabric_icon || ''
  const fabricIcon = fabricIcons[fabricIconMetaField] || ''
  const soldOut = item.variants_inventory_count <= 0
  const onSale = item.compare_at_price > item.price
  const compare_at_price = Number(item.compare_at_price)
  const compareAtPrice = compare_at_price !== 0 ? Number(item.compare_at_price) : null
  const publishedAt = new Date(item.published_at)
  const publishedAtSeconds = publishedAt.getTime() / 1000 // convert to seconds
  const lowStockBadgeDelay = window.themeSettings?.low_stock_badge_delay || 2
  const delaySeconds = lowStockBadgeDelay * 3600 // convert hours to seconds
  const lowStockBadgeCutoff = publishedAtSeconds + delaySeconds
  const currentTime = new Date().getTime() / 1000 // convert to seconds
  const showLowStockBadge = currentTime >= lowStockBadgeCutoff && publishedAtSeconds !== 0
  const lowStock =
    !soldOut &&
    item.variants_inventory_count > 0 &&
    item.variants_inventory_count <= (window.themeSettings?.low_stock_badge_threshold || 10) &&
    showLowStockBadge
  let badgeBGColor = ''
  let badgeTextColor = ''
  let badgeText = ''
  onSale &&
    ((badgeBGColor = window.themeSettings?.sale_badge_bg_color || '#cd4926'),
    (badgeText = 'Sale'),
    (badgeTextColor = window.themeSettings?.sale_badge_text_color || '#ffffff'))
  soldOut &&
    ((badgeBGColor = window.themeSettings?.sold_out_badge_bg_color || '#DDDDDD'),
    (badgeText = 'Sold Out'),
    (badgeTextColor = window.themeSettings?.sold_out_badge_text_color || '#3D3C3D'))
  const hoverImage = item.meta?.algolia?.hover_image || ''

  return html`
    <a
      class="ais-hit ais-product group relative !m-0 min-w-[120px] overflow-hidden rounded-lg !p-0 text-left xl:rounded-2xl"
      href="${itemLink}"
      tabindex="0"
      onClick="${() => trackSearchAttribution(item)}"
    >
      <div class="relative h-full overflow-hidden rounded-lg text-left xl:rounded-2xl">
        ${soldOut || (onSale && !lowStock)
          ? html`
              <div
                class="absolute left-1 top-1 z-20 rounded-full !px-3 !py-1 xl:!left-2.5 xl:!top-2.5"
                style="color: ${badgeTextColor}; background-color: ${badgeBGColor};"
              >
                <div
                  class="!text-xxs !font-medium !leading-tight md:!text-xs [&_p:not(:last-child)]:!mr-1 [&_p]:!inline-block [&_p]:!text-xs [&_p]:!font-medium [&_p]:!leading-tight"
                >
                  ${badgeText}
                </div>
              </div>
            `
          : ''}
        <div
          class="!aspect-4/5"
          data-quick-add-image
        >
          <img
            class="h-full w-full object-cover object-center"
            src="${resizedImage(helpers.originalImage(hit), 300)}"
            srcset="${cardImageSrcset(helpers.originalImage(hit))}"
            sizes="${CARD_IMAGE_SIZES}"
            decoding="async"
            alt="${item.title}${item.title.includes('Bundle') && !item.title.includes('Gift Set')
              ? ' 25% Off Three, Pick 3 Get 25% Off'
              : ''}"
          />
          ${hoverImage !== '' && supportsHover()
            ? html`
                <img
                  data-hover-image
                  class="absolute left-0 top-0 z-10 h-full w-full object-cover object-center opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100"
                  src="${resizedImage(hoverImage, 300)}"
                  srcset="${cardImageSrcset(hoverImage)}"
                  sizes="${CARD_IMAGE_SIZES}"
                  decoding="async"
                  loading="lazy"
                  alt="${item.title}"
                />
              `
            : ''}
        </div>
        ${
          /*fabricIcon !== ''
          ? html`
              <div class="absolute right-1 top-1 z-10 max-w-[30px] xl:right-2 xl:top-2 xl:max-w-10">
                <span class="sr-only">${fabricIcon.name} Fabric</span>
                <img
                  src="${fabricIcon.image}"
                  alt="${fabricIcon.name}"
                  class="h-auto w-full object-cover"
                />
              </div>  
            `
          : ''*/ ''
        }
        ${lowStock && !soldOut && !onSale
          ? html`
              <div
                class="collection-inventory-badge !absolute !left-1 !top-1 z-10 !flex !items-center !gap-1.5 !rounded-full !px-3 !py-1 xl:!left-2 xl:!top-2"
                style="background-color: ${window.themeSettings?.low_stock_badge_bg_color || '#FF6B35'}; color: ${window
                  .themeSettings?.low_stock_badge_text_color || '#ffffff'};"
              >
                ${window.themeSettings?.low_stock_badge_svg_code
                  ? html`<div class="!flex !h-2.5 !w-2.5 !flex-shrink-0 !items-center !justify-center">
                      ${html([window.themeSettings.low_stock_badge_svg_code])}
                    </div>`
                  : ''}
                <span class="!font-galano !text-xxs !font-medium !leading-tight md:!text-xs"
                  >${window.themeSettings?.low_stock_badge_text || 'Low Stock'}</span
                >
              </div>
            `
          : ''}
      </div>

      <div class="grid-product__meta !mt-3.5 !p-0 !text-center">
        ${tag
          ? html`
              <div class="!mx-auto !mb-2 w-fit rounded-[100px] border border-solid border-mist !px-2.5 !py-1">
                <p
                  class="!mb-0 text-center font-galano !text-xs font-medium capitalize !leading-tight !tracking-normal text-[var(--text-color)]"
                >
                  ${tag}
                </p>
              </div>
            `
          : ''}
        ${productName
          ? html`
              <h3
                data-cy="search-product-card-title"
                class="!mb-1 text-center font-galano text-base font-medium leading-tight text-[var(--text-color)] xl:!text-lg"
              >
                ${productName}
              </h3>
            `
          : ''}
        ${productCategory
          ? html`
              <p
                class="!mb-1.5 text-center !font-galano !text-sm !font-medium !leading-normal text-[var(--text-color)]"
              >
                ${productCategory}
              </p>
            `
          : ''}
        <p class="ais-hit--price !m-0 !flex justify-center gap-1 !text-center">
          ${compareAtPrice &&
          compareAtPrice !== 0 &&
          compareAtPrice !== null &&
          html`
            <span
              class="ais-hit--price-striked !my-0 !ml-0 !mr-1.5 !font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)] line-through"
              ><span>${helpers.displayStrikedPrice(item.price, item.compare_at_price)}</span></span
            >
          `}
          ${item.variants_min_price !== item.variants_max_price
            ? html`
                <b
                  class="holiday-text-primary !font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)]"
                  >from ${formatPrice(item.variants_min_price)}</b
                >
              `
            : html`
                <b class="!font-galano !text-sm !font-medium !leading-normal !text-[var(--text-color)]"
                  >${helpers.displayPrice(hit, item._distinct)}</b
                >
              `}
        </p>
      </div>
    </a>
  `
}

function formatPrice(price) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)
}

// Custom navigation API to detect URL param changes for billboard re-rendering
;(() => {
  let oldPushState = history.pushState
  let oldReplaceState = history.replaceState
  let previousParams = new URLSearchParams(window.location.search)

  function getParamsState() {
    const state = { q: previousParams.get('q'), page: previousParams.get('page') }

    // Get all parameters that start with 'refinementList'
    for (const [key, value] of previousParams.entries()) {
      if (key.startsWith('refinementList')) {
        state[key] = value
      }
    }

    return state
  }

  function checkParamsChanged(newParams) {
    const oldState = getParamsState()
    const newState = {
      q: newParams.get('q'),
      page: newParams.get('page'),
    }

    // Get all parameters that start with 'refinementList'
    for (const [key, value] of newParams.entries()) {
      if (key.startsWith('refinementList')) {
        newState[key] = value
      }
    }

    // Check if any refinementList parameters have changed
    const refinementListChanged =
      Object.keys(oldState).some((key) => key.startsWith('refinementList') && oldState[key] !== newState[key]) ||
      Object.keys(newState).some((key) => key.startsWith('refinementList') && !(key in oldState))

    const hasChanged = refinementListChanged || oldState.q !== newState.q || oldState.page !== newState.page

    previousParams = newParams
    return { oldState, newState, hasChanged }
  }

  history.pushState = function pushState() {
    let ret = oldPushState.apply(this, arguments)
    const newParams = new URLSearchParams(window.location.search)
    const { oldState, newState, hasChanged } = checkParamsChanged(newParams)
    if (hasChanged) {
      window.dispatchEvent(
        new CustomEvent('locationchange', {
          detail: { oldState, newState },
        })
      )
      // Rerender billboards on page change
      if (oldState.page !== newState.page) {
        removeBillboards()
        renderBillboards()
        removeUGCBillboards()
        removeBenefitsBillboards()
        renderNewBillboards()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    window.dispatchEvent(new Event('pushstate'))
    return ret
  }

  history.replaceState = function replaceState() {
    let ret = oldReplaceState.apply(this, arguments)
    const newParams = new URLSearchParams(window.location.search)
    const { oldState, newState, hasChanged } = checkParamsChanged(newParams)
    if (hasChanged) {
      window.dispatchEvent(
        new CustomEvent('locationchange', {
          detail: { oldState, newState },
        })
      )
      // Rerender billboards on page change
      if (oldState.page !== newState.page) {
        removeBillboards()
        renderBillboards()
        removeUGCBillboards()
        removeBenefitsBillboards()
        renderNewBillboards()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    window.dispatchEvent(new Event('replacestate'))
    return ret
  }

  window.addEventListener('popstate', () => {
    const newParams = new URLSearchParams(window.location.search)
    const { oldState, newState, hasChanged } = checkParamsChanged(newParams)
    if (hasChanged) {
      window.dispatchEvent(
        new CustomEvent('locationchange', {
          detail: { oldState, newState },
        })
      )
    }
  })
})()

// Example usage:
window.addEventListener('locationchange', function (event) {
  const { oldState, newState } = event.detail

  // Check for any refinementList parameter changes
  const oldRefinementKeys = Object.keys(oldState).filter((key) => key.startsWith('refinementList'))
  const newRefinementKeys = Object.keys(newState).filter((key) => key.startsWith('refinementList'))

  const searchQueryChanged = oldState.q !== newState.q
  const refinementCountChanged = oldRefinementKeys.length !== newRefinementKeys.length
  const refinementValuesChanged = oldRefinementKeys.some((key) => oldState[key] !== newState[key])
  const newRefinementsAdded = newRefinementKeys.some((key) => !(key in oldState))

  if (searchQueryChanged || refinementCountChanged || refinementValuesChanged || newRefinementsAdded) {
    removeBillboards()
    renderBillboards()
    removeUGCBillboards()
    removeBenefitsBillboards()
    renderNewBillboards()
    pendingCollectionFilterAnnouncement = true
  }
})

document.addEventListener(
  'algolia.instantSearch.onRender',
  function () {
    // Expand all facet accordions by setting checkbox state directly instead of
    // clicking labels — clicking causes the associated sr-only checkbox to
    // receive focus and triggers scroll-into-view, jumping the viewport.
    document.querySelectorAll('.ais-facet--header').forEach((element) => {
      const checkboxId = element.getAttribute('for')
      if (checkboxId) {
        const checkbox = document.getElementById(checkboxId)
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true
        }
      }
    })

    // Associate the collection live region with the search input and filter
    // checkboxes so screen readers surface the description contextually.
    const collectionSearchInput = document.querySelector('.ais-search-box-container input[type="search"]')
    if (collectionSearchInput) {
      collectionSearchInput.setAttribute('aria-describedby', COLLECTION_FILTER_RESULTS_STATUS_ID)
    }

    document.querySelectorAll('.ais-dropdown-checkbox, [data-algolia-facet-item]').forEach((checkbox) => {
      checkbox.setAttribute('aria-describedby', COLLECTION_FILTER_RESULTS_STATUS_ID)
    })
  },
  { once: true }
)

// Transform current refined value chip labels when they appear in the DOM.
function processRefinedValueItem(item) {
  if (item.dataset.labelTransformed) return
  const link = item.querySelector('.ais-current-refined-values--link')
  if (!link) return

  if (link.dataset.attribute === 'named_tags.bucket') {
    // The value text is a text node directly inside the <div> wrapping the label,
    // e.g. <div><div class="...--label">Discount Amount</div>: 3 </div>
    // Find that trailing text node and append ' 0% Off'.
    const wrapper = link.querySelector('div')
    if (wrapper) {
      const textNode = Array.from(wrapper.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim())
      if (textNode && !textNode.textContent.includes('0% Off')) {
        textNode.textContent = textNode.textContent.trimEnd() + '0% Off'
      }
    }
  }

  if (link.dataset.attribute === 'named_tags.sale_category') {
    const wrapper = link.querySelector('div')
    if (wrapper) {
      const textNode = Array.from(wrapper.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim())
      if (textNode) {
        textNode.textContent = textNode.textContent.replace(/-/g, '/')
      }
    }
  }

  item.dataset.labelTransformed = 'true'
}

;(function watchCurrentRefinedValuesContainer() {
  const bodyObserver = new MutationObserver(function () {
    const container = document.querySelector('.ais-current-refined-values-container')
    if (!container) return
    bodyObserver.disconnect()

    // Process any items already present
    container.querySelectorAll('.ais-current-refined-values--item').forEach(processRefinedValueItem)

    // Watch for future additions
    const containerObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return
          if (node.classList.contains('ais-current-refined-values--item')) {
            processRefinedValueItem(node)
          } else {
            node.querySelectorAll('.ais-current-refined-values--item').forEach(processRefinedValueItem)
          }
        })
      })
    })
    containerObserver.observe(container, { childList: true, subtree: true })
  })
  bodyObserver.observe(document.body, { childList: true, subtree: true })
})()

// Sort named_tags.bucket facet items numerically (ascending) whenever the list changes.
;(function watchBucketFacetList() {
  const bodyObserver = new MutationObserver(function () {
    const facetContainer = document.querySelector('.ais-facet-named_tags\\.bucket-container')
    if (!facetContainer) return
    bodyObserver.disconnect()

    const listObserver = new MutationObserver(function () {
      const list = facetContainer.querySelector('.ais-RefinementList-list')
      if (!list) return
      const items = Array.from(list.children)
      const sorted = items.slice().sort(function (a, b) {
        const aId = a.querySelector('input')?.id ?? ''
        const bId = b.querySelector('input')?.id ?? ''
        return parseFloat(aId.replace('algolia-filter-', '') || 0) - parseFloat(bId.replace('algolia-filter-', '') || 0)
      })
      const alreadySorted = sorted.every((item, i) => item === items[i])
      if (alreadySorted) return
      listObserver.disconnect()
      sorted.forEach(function (item) {
        list.appendChild(item)
      })
      listObserver.observe(facetContainer, { childList: true, subtree: true })
    })
    listObserver.observe(facetContainer, { childList: true, subtree: true })
  })
  bodyObserver.observe(document.body, { childList: true, subtree: true })
})()

document.addEventListener('algolia.instantSearch.onRender', function () {
  if (!pendingCollectionFilterAnnouncement) return
  pendingCollectionFilterAnnouncement = false
  announceCollectionFilterResults()
})

// Create a MutationObserver to watch for the product grid
const observer = new MutationObserver((mutations, observer) => {
  for (const mutation of mutations) {
    if (mutation.type === 'childList') {
      const productGrid = document.querySelector('.ais-Hits-list')
      if (productGrid) {
        renderBillboards()
        renderNewBillboards()
        renderPersonalizationBlock()
        observer.disconnect() // Stop observing once we've found the element
        break
      }
    }
  }
})

// Start observing the document with the configured parameters
observer.observe(document.body, {
  childList: true,
  subtree: true,
})

document.addEventListener('DOMContentLoaded', () => {
  // Clear button: in non-detached (desktop) mode Algolia doesn't reliably flush
  // the input value after its internal state clear, so we do it explicitly.
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.aa-ClearButton')) return
    const input = document.querySelector('input.aa-Input[type="search"]')
    if (input) {
      input.value = ''
      input.dispatchEvent(new InputEvent('input', { bubbles: true }))
      input.focus()
    }
  })

  // Enter on aa-Input (desktop / non-detached): Algolia intercepts the form
  // submit event and calls onSubmit, but the Shopify hook layer doesn't always
  // wire that through. Intercept Enter in the capture phase before Algolia, but
  // only when no panel item is keyboard-selected (aria-selected="true").
  const getAlgoliaRedirectUrl = () => {
    const section = document.querySelector('[data-autocomplete-source-id="redirectUrlPlugin"]')
    // Find the first child item(nested many levels deep) with the class 'aa-ItemLink' and a valid anchor tag
    const itemLink = section?.querySelector('.aa-ItemLink')
    return itemLink?.href || null
  }

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter') return
      if (!e.target.matches('input.aa-Input[type="search"]')) return
      const activeItem = document.querySelector('[data-algolia-search] .aa-Item[aria-selected="true"]')
      if (activeItem) return
      const query = e.target.value.trim()
      const redirectUrl = getAlgoliaRedirectUrl()
      console.log({
        location: 'algolia keydown event',
        query,
        redirectUrl,
      })
      if (!!redirectUrl) {
        e.preventDefault()
        window.location.href = redirectUrl
      } else if (query) {
        e.preventDefault()
        window.location.href = `/search?q=${encodeURIComponent(query)}`
      }
    },
    true
  )

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return

    const target = event.target
    if (!(target instanceof HTMLElement)) return

    if (target.matches('.ais-dropdown-checkbox, [data-algolia-facet-item], .ss-filter-menu-checkbox')) {
      event.preventDefault()
      target.click()
      return
    }

    if (target.matches('.algolia-accordion-label, .algolia-color-option, [data-ais-facets-button]')) {
      event.preventDefault()
      target.click()
    }
  })

  function observeFacetsMenu() {
    const facetsMenuObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const facetsMenu = document.querySelector('[data-ais-facets-menu]')
          if (facetsMenu) {
            if (!facetsMenu.querySelector('[data-algolia-submit-button]')) {
              const submitButton = document.createElement('button')
              submitButton.className =
                'holiday-bg-primary holiday-border-primary-new holiday-button-primary hover:mintDark block w-full rounded-lg border-2 border-solid border-mint bg-mint px-12 py-3 text-center text-xl font-semibold leading-[125%] text-galaxy transition-colors hover:border-mintDark hover:bg-mintDark xl:hidden'
              submitButton.setAttribute('data-algolia-submit-button', '')
              submitButton.textContent = 'Submit'

              submitButton.addEventListener('click', () => {
                facetsMenu.classList.remove('ais-facets__shown')
              })

              const staticLinks = `
                <ul class="!py-5 !m-0 !list-none !space-y-4">
                  <li>
                    <a class="holiday-text-primary !text-sm !font-semibold !leading-[125%] !text-[#3D3C3D]" href="https://littlesleepies.com/pages/sizing-guide">
                      Size Guide
                    </a>
                  </li>
                
                  <li>
                    <a class="holiday-text-primary !text-sm !font-semibold !leading-[125%] !text-[#3D3C3D]" href="https://littlesleepies.com/pages/our-fabrics">
                      Our Innovative Fabrics
                    </a>
                  </li>
                
                  <li>
                    <a class="holiday-text-primary !text-sm !font-semibold !leading-[125%] !text-[#3D3C3D]" href="https://littlesleepies.com/blogs/news/tagged/parenting-hacks">
                      Parenting Hacks
                    </a>
                  </li>
                </ul>              
              `

              const staticLinksElement = document.createElement('div')
              staticLinksElement.innerHTML = staticLinks
              facetsMenu.appendChild(submitButton)
              facetsMenu.appendChild(staticLinksElement)
              facetsMenuObserver.disconnect() // Stop observing once button is added
            }
          }
        }
      }
    })

    facetsMenuObserver.observe(document.body, { childList: true, subtree: true })
  }

  observeFacetsMenu()

  // Create a MutationObserver to watch for the clear button
  const observer = new MutationObserver((mutations, obs) => {
    const clearButton = document.querySelector('.aa-ClearButton')
    if (clearButton) {
      clearButton.innerHTML = 'Clear'
      // Stop observing once we've found and updated the button
      obs.disconnect()
    }
  })

  // Start observing the document body for changes
  observer.observe(document.body, {
    childList: true, // watch for child elements being added or removed
    subtree: true, // watch all descendants, not just direct children
  })

  const svg = `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="11"
      viewBox="0 0 10 11"
      fill="none"
    >
      <path d="M9.08366 1.4165L0.916992 9.58317" stroke="#403940" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M0.916992 1.4165L9.08366 9.58317" stroke="#403940" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>  
  `

  const cancelButtonObserver = new MutationObserver((mutations, obs) => {
    const cancelButton = document.querySelector('.aa-DetachedCancelButton')
    if (cancelButton) {
      cancelButton.innerHTML = svg
      // Stop observing once we've found and updated the button
      obs.disconnect()
    }
  })

  // Start observing the document body for changes
  cancelButtonObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })
})

document.addEventListener('algolia.hooks.initialize', function () {
  initHeaderFilterResultAnnouncements()
  ensureCollectionFilterStatusRegion()
  visitorStatus = checkVisitorStatus()
  // Change category page identifier to collection handle
  algoliaShopify.hooks.registerHook('beforeInstantSearchFiltersString', function (defaultFilter) {
    if (defaultFilter && defaultFilter.includes('collection_ids')) {
      const newFilter = `available:true AND ${defaultFilter}`
      const collectionIDFilterRegex = /collection_ids:"\w+"/g
      const matches = window.location.pathname.match(/\/collections\/([^/]+)/i)
      const collectionHandle = matches && matches.length === 2 ? matches[1] : null

      if (collectionHandle) {
        return newFilter.replace(collectionIDFilterRegex, 'collections:' + collectionHandle)
      }
    }

    return newFilter
  })

  // Facets restricted to specific collections only.
  // Key: facet escapedName (partial match). Value: array of collection handles where the facet should appear.
  // Facets listed here will be hidden on all other collections and search.
  const SALE_COLLECTIONS = [
    '30-off',
    '40-off',
    '65-off',
    'sale',
    'little-big-sale',
    'little-big-sale-favorites',
    'play-on-sale',
    'zippies-on-sale',
    'two-piece-pajamas-on-sale',
    'adult-pajamas-on-sale',
    'accessories-on-sale',
    'characters-on-sale',
    'baby-styles-on-sale',
    'toddler-styles-on-sale',
    'kids-styles-on-sale',
  ]

  const COLLECTION_FACET_RESTRICTIONS = {
    'named_tags.bucket': SALE_COLLECTIONS,
    'named_tags.sale_category': SALE_COLLECTIONS,
  }

  function isFacetAllowedForCurrentCollection(escapedName) {
    const currentHandle = (window.location.pathname.match(/\/collections\/([^/]+)/i) || [])[1] || null
    for (const [facetKey, allowedCollections] of Object.entries(COLLECTION_FACET_RESTRICTIONS)) {
      if (escapedName.includes(facetKey)) {
        return currentHandle && allowedCollections.includes(currentHandle)
      }
    }
    return true
  }

  // Build a title → attribute map so label transformers can key on the raw attribute name
  algoliaShopify.hooks.registerHook('beforeInstantSearchMainTemplate', function (_defaultTemplate, data, html) {
    window._algoliaFacetMap = {}
    data.facets.forEach(({ escapedName, title }) => {
      window._algoliaFacetMap[title] = escapedName
    })

    return html`
      <div class="ais-page !my-0 !px-0">
        <div class="xl:flex xl:gap-8">
          <div
            class="fixed bottom-0 left-0 z-20 hidden h-24 w-full xl:block"
            style="background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFFFFF 100%)"
          ></div>
          <div
            class="algolia-sticky-filter hide-scrollbar sticky top-0 z-[39] !mb-6 flex flex-row-reverse xl:top-4 xl:z-[1] xl:!mb-0 xl:h-fit xl:max-h-[calc(100vh-2rem)] xl:min-w-fit xl:flex-col xl:justify-start xl:overflow-y-scroll xl:!pb-24"
          >
            <div class="ais-input !my-0 !rounded-none xl:!rounded-md">
              <div class="ais-search-box-container"></div>
              <div class="ais-input-button">
                <div class="ais-clear-input-icon"></div>
              </div>
            </div>
            <div>
              <div
                class="holiday-bg-primary ais-facets-button !m-0 !border-y-0 !border-l-0 !border-r !border-solid !border-mint !bg-mintLight !p-4 xl:!hidden"
                data-ais-facets-button
                role="button"
                tabindex="0"
              >
                Show filters
              </div>
              <div
                class="hide-scrollbar ais-facets holiday-bg-secondary !absolute left-0 h-[calc(100vh-53px)] overflow-y-auto bg-white !px-4 xl:!static xl:h-auto xl:!px-0"
                data-ais-facets-menu
              >
                <div class="ais-search-header !mx-0 !my-2.5 !p-0">
                  <div class="ais-stats-container !hidden"></div>
                  <div class="ais-change-display !hidden">
                    <span class="ais-change-display-block ais-change-display-selected"
                      ><i class="fa fa-th-large"></i
                    ></span>
                    <span class="ais-change-display-list"><i class="fa fa-th-list"></i></span>
                  </div>
                  <div class="holiday-text-primary ais-sort holiday-bg-secondary flex !hidden">
                    ${data.multipleSortOrders
                      ? html`
                          ${algoliaShopify.translations.sortBy}
                          <span class="ais-sort-orders-container grow"></span>
                        `
                      : html` ${algoliaShopify.translations.sortBy} ${algoliaShopify.translations.relevance} `}
                  </div>
                </div>
                <div class="relative">
                  <div class="ais-clear-refinements-container !absolute right-0 top-0 z-[1]"></div>
                </div>
                <div class="ais-current-refined-values-container"></div>
                ${data.facets.map((facet) => {
                  const { type, escapedName, title } = facet
                  let formattedType = ''
                  if (escapedName.includes('named_tags.color')) {
                    formattedType = 'color'
                  } else if (escapedName.includes('options.size')) {
                    formattedType = 'size'
                  }
                  const isHidden = !isFacetAllowedForCurrentCollection(escapedName)

                  return html`
                    <div
                      class="ais-facet-dropdown-wrapper ais-facet-${type} ais-facet-${escapedName}${formattedType !== ''
                        ? ` ais-os-facet-${formattedType}`
                        : ''}${isHidden ? ' !hidden' : ''}"
                    >
                      <input
                        class="ais-dropdown-checkbox sr-only !block"
                        type="checkbox"
                        id="${escapedName}"
                        name="dropdown"
                      />
                      <h3 class="algolia-accordion-label-header w-full">
                        <label
                          for="${escapedName}"
                          class="algolia-accordion-label ais-range-slider--header ais-facet--header ais-header holiday-bg-secondary holiday-text-primary holiday-stroke-primary cursor-pointer !border-0 bg-white"
                        >
                          ${title}
                          <svg
                            class="ais-accordion-plus !h-4 !w-4"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 9H17"
                              stroke="#3D3C3D"
                              stroke-width="2"
                              stroke-linecap="round"
                              fill="#3D3C3D"
                            />
                            <path
                              d="M9 17L9 1"
                              stroke="#3D3C3D"
                              stroke-width="2"
                              stroke-linecap="round"
                              fill="#3D3C3D"
                            />
                          </svg>
                          <svg
                            class="ais-accordion-minus !h-4 !w-4"
                            width="18"
                            height="2"
                            viewBox="0 0 18 2"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 1H17"
                              stroke="#3D3C3D"
                              stroke-width="2"
                              stroke-linecap="round"
                              fill="#3D3C3D"
                            />
                          </svg>
                        </label>
                      </h3>
                      <div class="ais-facet-${escapedName}-container ais-facet-dropdown-container"></div>
                    </div>
                  `
                })}
              </div>
            </div>
          </div>
          <div class="ais-block z-30">
            <div class="ais-hits-container ais-results-as-block !px-4"></div>
          </div>
        </div>
        <div class="ais-pagination-container"></div>
      </div>
    `
  })

  window.algoliaShopify.hooks.registerHook('beforeInstantSearchConfigurationOptions', function (options) {
    const searchFilters = [BASE_PRODUCT_FILTER]

    let collectionFacetFilter = null
    let collectionRulesContextValue = null
    let collectionHandle = null

    // Detect if we're on a collection page and if InstantSearch is enabled for it
    // NOTE: Feel free to replace this collection page detection logic with your own
    const collectionPageEnabled =
      window.algoliaShopify.is_collection_results_page &&
      window.algoliaShopify.config.instant_search_enabled_on_collection

    if (collectionPageEnabled) {
      // Extract the collection handle from the current URL
      const matches = window.location.pathname.match(/\/collections\/([^/]+)/i)
      collectionHandle = matches && matches.length === 2 ? matches[1] : null

      // Determine which type of collection facet to apply: ID or handle
      if (window.algoliaShopify.config.collection_id_indexing) {
        collectionFacetFilter = window.algoliaShopify.current_collection_id
          ? `collection_ids:"${window.algoliaShopify.current_collection_id}"`
          : null
      } else {
        collectionFacetFilter = `collections:"${collectionHandle}"`
      }

      // Add collection filter to the overall list of filters
      if (collectionFacetFilter) {
        searchFilters.push(collectionFacetFilter)
      }

      // Define rule context value based on configuration
      collectionRulesContextValue = window.algoliaShopify.config.collection_id_query_rules
        ? window.algoliaShopify.current_collection_id
        : collectionHandle
    }

    // Handle stock policy filtering based on configuration
    // NOTE: Any changes you need to make to your inventory availability filters can be swapped in here
    let stockPolicyFilter = null
    if (window.algoliaShopify.config.stock_policy) {
      const policy = window.algoliaShopify.config.stock_policy

      if (policy === 'deny') {
        stockPolicyFilter = 'inventory_quantity > 0'
      } else if (policy === 'continue') {
        stockPolicyFilter = 'inventory_available:true'
      }

      if (stockPolicyFilter) {
        searchFilters.push(stockPolicyFilter)
      }
    }

    // Define the custom search function to inject query parameters before each search
    const searchFunction = function (searchFunctionHelper) {
      const page = searchFunctionHelper.getPage()

      // Set the distinct parameter if configured (groups items at a product level
      if (window.algoliaShopify.instantsearch.distinct) {
        searchFunctionHelper.setQueryParameter('distinct', true)
      }

      // Combine all filters and trigger any hooks that modify them
      const searchFilterString = searchFilters.join(' AND ')
      const searchFilterUserOptions = window.algoliaShopify.hooks.triggerHooks(
        'beforeInstantSearchFiltersString',
        searchFilterString
      )

      // Apply filters to the search query if any are present
      if (searchFilterUserOptions.length) {
        searchFunctionHelper.setQueryParameter('filters', searchFilterUserOptions)
      }

      // Set query rule contexts to influence Algolia Query Rules
      const ruleContexts = [pageHandle, visitorStatus, ...utmParams]

      // NOTE: This is where you can add Little Sleepies' custom rule logic

      // Necessary 'if' conditionals to ensure the filters that power your collection pages pass through
      if (collectionRulesContextValue) {
        ruleContexts.push(collectionRulesContextValue.toString())
      }

      if (collectionPageEnabled) {
        ruleContexts.push('shopify_default_collection')
      }

      searchFunctionHelper.setQueryParameter('ruleContexts', ruleContexts)
      // -----------------------------------------------

      // Maintain current pagination
      searchFunctionHelper.setPage(page)

      // Trigger the search
      searchFunctionHelper.search()
    }

    // Return updated options with the custom search function
    return {
      ...options,
      searchFunction,
    }
  })

  algoliaShopify.hooks.registerHook('beforeISStartAddWidgetArray', function () {
    // Please be aware not all widgets are available
    const { searchBox, configure, pagination } = window.algoliaShopify.externals.widgets
    const billboardJson = document.querySelector('[data-billboards-json]')
    let billboardJsonData
    let billboards
    let hitsPerPage = 48
    let ugcBillboards = []
    let ugcBillboardPositions = []
    let benefitsBillboards = []
    let benefitsBillboardPositions = []

    if (!!billboardJson) {
      billboardJsonData = JSON.parse(billboardJson.textContent)
      // const positions = billboardJsonData.positions || []
      // billboards = billboardJsonData.billboards
      // positions.forEach((position, index) => {
      //   if (position <= 48) {
      //     hitsPerPage -= billboards[index].desktop_column_span || 1
      //   }
      // })
      ugcBillboards = billboardJsonData.ugc_billboards
      ugcBillboardPositions = billboardJsonData.ugc_billboard_positions
      benefitsBillboards = billboardJsonData.benefits_billboards
      benefitsBillboardPositions = billboardJsonData.benefits_billboard_positions
      if (ugcBillboardPositions?.length > 0) {
        ugcBillboardPositions.forEach((position, index) => {
          if (position <= 48) {
            hitsPerPage -= isMobile ? 2 : 1
          }
        })
      }
      if (benefitsBillboardPositions?.length > 0) {
        benefitsBillboardPositions.forEach((position, index) => {
          if (position <= 48) {
            hitsPerPage -= 2
          }
        })
      }
    }

    // const collectionTitle = document.querySelector('[data-collection-title]').data('collection-title')
    const searchPlaceholder = window.location.href.includes('search')
      ? 'Search Little Sleepies'
      : `Search this collection`

    const searchBoxWidget = searchBox({
      container: '.ais-search-box-container',
      placeholder: searchPlaceholder,
    })

    const ruleContexts = [pageHandle, visitorStatus, ...utmParams]

    const configureWidget = configure({
      filters: 'available:true',
      facetingAfterDistinct: false,
      hitsPerPage,
      analyticsTags,
      ruleContexts,
    })

    return [searchBoxWidget, configureWidget]
  })

  // Facet Item - Clickable element that can apply a filter
  algoliaShopify.hooks.registerHook('beforeInstantSearchFacetItemTemplate', function (_defaultTemplate, item, html) {
    if (item.attribute === 'named_tags.color') {
      const color = colors[item.value]
      const colorId = `color-filter-${handleize(color.name)}`
      return html`
        <div class="!m-0 w-fit">
          <label
            class="algolia-color-option ${item.isRefined
              ? '!border-galaxy'
              : '!border-transparent'} !m-0 !flex !h-8 !w-8 !cursor-pointer !items-center !justify-center !rounded-full !border !border-solid !p-0"
            for="${colorId}"
          >
            <input
              id="${colorId}"
              type="checkbox"
              class="peer !sr-only"
              data-algolia-facet-item
              aria-label="${color.name} color filter"
              name="${color.name}"
              value="${color.name}"
              checked="${item.type === 'disjunctive' && item.isRefined}"
            />
            ${color.image
              ? html`
                  <img
                    src="${color.image}"
                    alt=""
                    aria-hidden="true"
                    class="!h-7 !w-7 !rounded-full !object-cover !object-center"
                  />
                `
              : html`
                  <div
                    class="${color.name === 'White'
                      ? '!border-galaxy'
                      : '!border-transparent'} !h-7 !w-7 !rounded-full !border !border-solid"
                    aria-hidden="true"
                    style="background-color: ${color.hex};"
                  ></div>
                `}
            <span
              class="!sr-only"
              aria-hidden="true"
              >${color.name}</span
            >
          </label>
        </div>
      `
    } else if (item.attribute === 'options.size') {
      return html`
        <div class="!m-0">
          <label
            for="size-filter-${handleize(item.value)}"
            class="contents"
          >
            <input
              id="size-filter-${handleize(item.value)}"
              type="checkbox"
              class="algolia-size-input peer !sr-only"
              data-algolia-facet-item
              aria-label="${item.value} size filter"
              name="${handleize(item.value)}"
              value="${item.value}"
              checked="${item.type === 'disjunctive' && item.isRefined}"
            />
            <div
              aria-hidden="true"
              class="algolia-size-box !m-0 !flex !h-full !items-center !justify-center !rounded-[3px] !border !border-solid !border-mist !p-2 !text-center !text-galaxy !transition-colors duration-300 hover:!cursor-pointer hover:!border-galaxy peer-checked:!border-galaxy peer-checked:!bg-galaxy peer-checked:!font-bold peer-checked:!text-white"
            >
              <span class="!font-galano !text-base !font-medium !normal-case !leading-tight !tracking-normal">
                ${item.value.replace(/ months/g, 'm')}
              </span>
            </div>
          </label>
        </div>
      `
    } else {
      const checkboxId = `algolia-filter-${handleize(item.label)}`
      let labelText = item.label.charAt(0).toUpperCase() + item.label.slice(1)
      if (item.attribute === 'named_tags.bucket') labelText += '0% Off'
      if (item.attribute === 'named_tags.sale_category') labelText = labelText.replace(/-/g, '/')
      return html`
        <label
          class="algolia-checkbox-option ss-filter-menu-checkbox-container"
          for="${checkboxId}"
        >
          <input
            id="${checkboxId}"
            type="checkbox"
            class="ss-filter-menu-checkbox"
            aria-label="${labelText} product filter"
            checked="${item.isRefined}"
            data-algolia-facet-item
          />
          <span
            class="ss-filter-menu-styled-checkbox"
            aria-hidden="true"
          ></span>
          <span class="ss-filter-menu-checkbox-text capitalize">${labelText}</span>
        </label>
      `
    }
  })

  // Product Card Template
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchProductTemplate',
    function (_defaultTemplate, hit, html, components) {
      return createInstantSearchProductCard(hit, html, algoliaShopify.helpers)
    }
  )

  algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', function (options) {
    options.openOnFocus = true
    options.detachedMediaQuery = '(max-width: 680px)'
    options.panelContainer = '[data-algolia-search]'
    options.placeholder = 'Search Little Sleepies'

    options.onSubmit = ({ state }) => {
      if (state.query) {
        window.location.href = `/search?q=${encodeURIComponent(state.query)}`
      }
    }

    // When a panel anchor is moused-down, capture its href so we can navigate
    // via mouseup — the anchor may be removed from the DOM before click fires
    // because Algolia collapses the panel synchronously when the input blurs.
    let pendingPanelNavUrl = null
    let panelMousedownActive = false

    document.addEventListener(
      'mousedown',
      function (e) {
        const panelContainer = document.querySelector('[data-algolia-search]')
        if (!panelContainer) return
        const anchor = e.target.closest('a[href]')
        if (anchor && panelContainer.contains(anchor)) {
          panelMousedownActive = true
          pendingPanelNavUrl = anchor.href
        } else {
          panelMousedownActive = false
          pendingPanelNavUrl = null
        }
      },
      true
    )

    document.addEventListener(
      'mouseup',
      function () {
        if (pendingPanelNavUrl) {
          const url = pendingPanelNavUrl
          pendingPanelNavUrl = null
          panelMousedownActive = false
          window.location.href = url
        }
      },
      true
    )

    // Handle Enter key on a focused anchor inside the panel
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter') return
        const panelContainer = document.querySelector('[data-algolia-search]')
        const focused = document.activeElement
        if (panelContainer && panelContainer.contains(focused) && focused.tagName === 'A' && focused.href) {
          e.preventDefault()
          window.location.href = focused.href
        }
      },
      true
    )

    options.onStateChange = function ({ prevState, state, setIsOpen }) {
      if (!prevState.isOpen && state.isOpen) {
        requestAnimationFrame(function () {
          const detachedOverlay = document.querySelector('.aa-DetachedOverlay')
          if (detachedOverlay) {
            detachedOverlay.setAttribute('role', 'dialog')
            detachedOverlay.setAttribute('aria-modal', 'true')
            detachedOverlay.setAttribute('aria-label', 'Search')
          }
        })

        // Use MutationObserver to catch .aa-Panel as soon as it's added to the DOM
        const panelObserver = new MutationObserver(function (mutations, observer) {
          mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
              if (node.nodeType !== 1) return
              const panels =
                node.classList && node.classList.contains('aa-Panel')
                  ? [node]
                  : Array.from(node.querySelectorAll('.aa-Panel'))
              panels.forEach(function (panel) {
                observer.disconnect()
              })
            })
          })
        })
        panelObserver.observe(document.body, { childList: true, subtree: true })
      }
      if (prevState.isOpen && !state.isOpen) {
        // If this close was triggered by a mousedown on a panel anchor,
        // skip reopening — navigation is handled by the mouseup listener above.
        if (panelMousedownActive) return
        requestAnimationFrame(function () {
          const panelContainer = document.querySelector('[data-algolia-search]')
          const focused = document.activeElement
          if (panelContainer && panelContainer.contains(focused) && focused.tagName !== 'INPUT') {
            setIsOpen(true)
          }
        })
      }
    }
    const { searchClient, querySuggestionsPlugin } = window.algoliaShopify
    const { getAlgoliaResults } = window.algoliaShopify.externals

    options.plugins.push(querySuggestionsPlugin)

    const filteredCollectionRuleContexts = ['filtered-collections', visitorStatus, ...utmParams]
    const filteredCollectionsPlugin = {
      name: 'filtered-collections-plugin',
      getSources({ query }) {
        return [
          {
            sourceId: 'filteredCollections',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: 'shopify_collections',
                    query,
                    params: {
                      filters: 'meta.algolia.show_in_search:true',
                      hitsPerPage: 4,
                      ruleContexts: filteredCollectionRuleContexts,
                      analyticsTags,
                    },
                  },
                ],
              })
            },
            templates: {
              item({ item, components, html }) {
                const itemLink = `${window.Shopify.routes.root}collections/${item.handle}`
                return html`<a href="${itemLink}">
                  <p class="holiday-text-primary m-0 !font-galano text-base font-bold leading-tight">${item.title}</p>
                </a>`
              },
            },
          },
        ]
      },
    }

    const trendingProductsRuleContexts = ['trending-products', visitorStatus, ...utmParams]
    const trendingProductsPlugin = {
      name: 'custom-trending-products-plugin',
      getSources({ query }) {
        return [
          {
            sourceId: 'trendingProducts',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: 'shopify_products',
                    query,
                    params: {
                      ruleContexts: trendingProductsRuleContexts,
                      analyticsTags,
                      filters: 'available:true',
                      hitsPerPage: 4,
                    },
                  },
                ],
              })
            },
            templates: {
              item({ item, components, html }) {
                const distinct = window.algoliaShopify.config.show_products
                const variantInfo = !distinct && item.objectID !== item.id ? `?variant=${item.objectID}` : ''
                const itemLink = `${window.Shopify.routes.root}products/${item.handle}/${variantInfo}`

                return createAutocompleteProductCard(
                  item,
                  html,
                  window.algoliaShopify.helpers,
                  itemLink,
                  window.algoliaShopify.helpers.handleItemClick
                )
              },
            },
          },
        ]
      },
    }

    const sleepResultsRuleContexts = ['sleep-search-results', visitorStatus, ...utmParams]
    const sleepSearchResultsPlugin = {
      name: 'custom-sleep-search-results-plugin',
      getSources({ query }) {
        return [
          {
            sourceId: 'sleepSearchResults',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: 'shopify_products',
                    query,
                    params: {
                      filters: `${BASE_PRODUCT_FILTER} AND named_tags.division:sleep`,
                      hitsPerPage: 4,
                      ruleContexts: sleepResultsRuleContexts,
                      analyticsTags,
                    },
                  },
                ],
              })
            },
            templates: {
              header({ html, state }) {
                return html`
                  <div class="mx-4 !mb-6 !mt-4 flex gap-2 xl:mx-0 xl:!mb-4 xl:!mt-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="23"
                      viewBox="0 0 20 23"
                      fill="none"
                    >
                      <path
                        d="M19.5724 15.1389C22.1582 7.51747 12.3893 -0.70969 7.09477 0.0487606C10.1402 2.35593 12.3571 8.58897 11.1024 12.5161C9.84888 16.4433 4.51019 20.4849 0 18.3559C4.1616 23.7249 15.7367 26.4393 19.5712 15.1402L19.5724 15.1389Z"
                        fill="#CCE8F2"
                      />
                      <path
                        d="M15.1723 11.6583C15.183 11.6634 15.1938 11.6685 15.2045 11.6749C15.2248 11.6876 15.2475 11.699 15.2654 11.7156C15.294 11.7423 15.3239 11.7703 15.3454 11.8021C15.3788 11.8518 15.4337 11.8798 15.4731 11.9243C15.4946 11.9485 15.5245 11.9676 15.5519 11.9866C15.5829 12.0083 15.614 12.0286 15.6462 12.049C15.6641 12.0617 15.6832 12.0732 15.7011 12.0859C15.7071 12.0897 15.7143 12.0935 15.7202 12.0974C15.7835 12.1304 15.8468 12.1648 15.9124 12.1966C15.9494 12.2144 15.9888 12.2259 16.0258 12.2399C16.0593 12.2526 16.0915 12.2653 16.1237 12.2768C16.144 12.2844 16.1655 12.2882 16.187 12.2946C16.2157 12.3022 16.2431 12.3112 16.273 12.3175C16.2909 12.3213 16.31 12.3226 16.3267 12.3264C16.3422 12.3302 16.3577 12.3353 16.3744 12.3391C16.3768 12.3391 16.3792 12.3417 16.3816 12.3417C16.4067 12.3442 16.4317 12.3468 16.4556 12.3506C16.4807 12.3544 16.5058 12.3608 16.5308 12.3659C16.5332 12.3659 16.5356 12.3659 16.538 12.3659C16.5834 12.3684 16.6287 12.3684 16.6729 12.3748C16.7779 12.3901 16.883 12.3786 16.9881 12.3811C17.0263 12.3811 17.0633 12.3773 17.1015 12.3735C17.1218 12.3722 17.1432 12.3684 17.1635 12.3659C17.1994 12.3608 17.2364 12.3557 17.2722 12.3493C17.3104 12.3417 17.3486 12.3341 17.3868 12.3252C17.4214 12.3175 17.4572 12.3112 17.4918 12.301C17.5312 12.2908 17.5694 12.2755 17.6076 12.2641C17.6279 12.2577 17.6506 12.2526 17.6709 12.2463C17.69 12.2399 17.7079 12.2335 17.7258 12.2272C17.7652 12.2132 17.8034 12.1979 17.8416 12.1826C17.8595 12.175 17.8762 12.1661 17.893 12.1584C17.9419 12.1343 17.992 12.1126 18.041 12.0885C18.072 12.0719 18.1019 12.0541 18.1329 12.0363C18.1711 12.0134 18.2081 11.9892 18.2463 11.965C18.2654 11.9536 18.2857 11.9421 18.3048 11.9307C18.3502 11.9077 18.3944 11.8836 18.4397 11.8619C18.4529 11.8556 18.4684 11.853 18.4827 11.8492C18.4922 11.8467 18.5018 11.8416 18.5102 11.8416C18.5281 11.8428 18.546 11.8467 18.5615 11.853C18.5818 11.8607 18.6021 11.8696 18.6164 11.8836C18.6343 11.9001 18.6486 11.9205 18.6582 11.9421C18.6856 12.0032 18.6821 12.0643 18.6498 12.1241C18.6427 12.1368 18.6307 12.1495 18.6212 12.161C18.5937 12.1915 18.5663 12.2246 18.5352 12.2526C18.5018 12.2832 18.4636 12.3086 18.4278 12.3353C18.4063 12.3519 18.3848 12.3684 18.3621 12.3837C18.3418 12.3964 18.3215 12.4091 18.3012 12.4219C18.2941 12.427 18.2881 12.4333 18.2774 12.441C18.3012 12.4779 18.3251 12.5135 18.3478 12.5491C18.3633 12.5733 18.3776 12.5988 18.3932 12.6242C18.4099 12.6497 18.4278 12.6738 18.4445 12.6993C18.4564 12.7171 18.466 12.7362 18.4779 12.7553C18.4946 12.782 18.5125 12.8087 18.5269 12.8355C18.5746 12.9207 18.62 13.006 18.6665 13.0912C18.6725 13.1027 18.6737 13.118 18.6749 13.1307C18.6761 13.1498 18.6749 13.1689 18.6749 13.188C18.6773 13.2172 18.6642 13.2401 18.6486 13.2643C18.6307 13.2923 18.6069 13.3088 18.5782 13.3216C18.5639 13.3292 18.546 13.3343 18.5293 13.3343C18.4982 13.3343 18.466 13.3407 18.4349 13.3267C18.4266 13.3228 18.4158 13.3216 18.4087 13.3165C18.3824 13.2948 18.3562 13.2745 18.3406 13.2439C18.3335 13.2299 18.3203 13.2185 18.3108 13.2058C18.2905 13.179 18.2714 13.151 18.2511 13.1243C18.2427 13.1129 18.2344 13.1014 18.2248 13.09C18.2105 13.0721 18.1938 13.0556 18.1807 13.0391C18.158 13.0098 18.1353 12.9793 18.1126 12.95C18.1043 12.9385 18.0947 12.9271 18.0864 12.9156C18.0673 12.8914 18.047 12.8685 18.0291 12.8444C18.01 12.8164 17.9932 12.7884 17.9753 12.7604C17.955 12.7298 17.9347 12.6993 17.9144 12.6687C17.9097 12.6611 17.9073 12.6522 17.9025 12.6446C17.8965 12.6344 17.893 12.6267 17.8762 12.6344C17.8464 12.6484 17.813 12.6586 17.7807 12.6687C17.7569 12.6777 17.733 12.6878 17.7079 12.6955C17.6852 12.7031 17.6602 12.7095 17.6375 12.7158C17.6076 12.7247 17.5766 12.7324 17.5468 12.7426C17.5336 12.7464 17.5205 12.7515 17.5074 12.7553C17.4895 12.7591 17.4727 12.7616 17.4548 12.7642C17.4417 12.7667 17.4298 12.7705 17.4178 12.7744C17.4226 12.7947 17.4262 12.8151 17.431 12.8355C17.4357 12.8558 17.4405 12.8749 17.4453 12.894C17.4453 12.8965 17.4465 12.8991 17.4465 12.9016C17.4489 12.9296 17.4513 12.9563 17.4548 12.9831C17.4584 13.0047 17.4644 13.0263 17.4704 13.0467C17.4704 13.048 17.4704 13.0505 17.4704 13.0518C17.4727 13.076 17.4751 13.1014 17.4787 13.1256C17.4823 13.1447 17.4883 13.1625 17.493 13.1803C17.493 13.1841 17.493 13.188 17.493 13.1918C17.4978 13.2172 17.5038 13.2427 17.5086 13.2694C17.5086 13.2707 17.5086 13.2732 17.5086 13.2758C17.5109 13.2999 17.5133 13.3254 17.5169 13.3496C17.5217 13.3865 17.5241 13.4247 17.5348 13.4616C17.542 13.4883 17.5396 13.5125 17.542 13.5379C17.542 13.543 17.5456 13.5468 17.5468 13.5519C17.5468 13.5557 17.5503 13.5608 17.5491 13.5646C17.5444 13.5888 17.542 13.613 17.5312 13.6334C17.5133 13.6677 17.4847 13.6893 17.4465 13.7021C17.4059 13.7148 17.3653 13.7097 17.3259 13.7072C17.3008 13.7059 17.2781 13.6881 17.2531 13.6766C17.2185 13.6601 17.2041 13.6283 17.1886 13.5977C17.1803 13.5786 17.1743 13.5583 17.1683 13.5392C17.1612 13.5188 17.154 13.4985 17.1468 13.4768C17.1421 13.4628 17.1397 13.4488 17.1361 13.4361C17.1289 13.4119 17.1206 13.3877 17.1134 13.3636C17.1098 13.3521 17.1074 13.3394 17.1039 13.3279C17.0955 13.2987 17.0871 13.2707 17.08 13.2414C17.0728 13.2147 17.0704 13.188 17.0633 13.1625C17.0573 13.1396 17.0477 13.118 17.0418 13.0951C17.0346 13.0734 17.0298 13.0505 17.0239 13.0276C17.0215 13.0187 17.0179 13.0085 17.0155 12.9996C17.0072 12.9703 16.9988 12.9423 16.9916 12.9131C16.9845 12.8889 16.9785 12.8634 16.9713 12.8355C16.9666 12.8355 16.9582 12.8355 16.951 12.8355C16.8687 12.838 16.7863 12.8456 16.7039 12.8418C16.649 12.8393 16.5941 12.8355 16.5392 12.8355C16.5368 12.8355 16.5332 12.8355 16.532 12.8355C16.5034 12.8253 16.4974 12.8291 16.495 12.8584C16.4938 12.8762 16.4867 12.8927 16.4819 12.9093C16.4783 12.922 16.4747 12.9334 16.4711 12.9462C16.4652 12.9729 16.4616 13.0009 16.4544 13.0276C16.4485 13.0505 16.4389 13.0721 16.4317 13.0951C16.4282 13.1065 16.4246 13.118 16.4234 13.1294C16.4198 13.1485 16.4186 13.1689 16.415 13.188C16.4114 13.207 16.4055 13.2249 16.4007 13.2452C16.3971 13.2592 16.3935 13.2745 16.3912 13.2885C16.3876 13.3101 16.3864 13.3318 16.3828 13.3534C16.3792 13.3737 16.3732 13.3928 16.3685 13.4119C16.3685 13.4132 16.3673 13.4157 16.3673 13.417C16.3649 13.4412 16.3649 13.4679 16.3589 13.4908C16.3494 13.5252 16.3482 13.5621 16.3231 13.5939C16.298 13.627 16.2658 13.6423 16.23 13.655C16.1906 13.6703 16.15 13.6652 16.1106 13.6613C16.0593 13.655 16.0175 13.6257 15.9936 13.5774C15.9805 13.5506 15.9817 13.5226 15.9757 13.4946C15.9626 13.4196 15.9721 13.3445 15.9697 13.2694C15.9697 13.2376 15.9745 13.2045 15.9769 13.1727C15.9793 13.1523 15.9829 13.132 15.9853 13.1116C15.9912 13.0683 15.9924 13.0251 16.0032 12.9831C16.0091 12.9589 16.0067 12.936 16.0103 12.9118C16.0139 12.8851 16.0211 12.8571 16.0258 12.8304C16.027 12.8215 16.0258 12.8125 16.0258 12.8036C16.027 12.7922 16.0306 12.7807 16.033 12.7693C16.0366 12.7566 16.033 12.7464 16.0199 12.7426C15.9996 12.7349 15.9769 12.7324 15.9566 12.7247C15.928 12.7158 15.9005 12.7069 15.8718 12.698C15.8444 12.6891 15.8157 12.6789 15.7883 12.6687C15.762 12.6586 15.7358 12.6471 15.7095 12.6344C15.6725 12.6166 15.6355 12.6 15.5985 12.5835C15.5913 12.5797 15.5841 12.5797 15.577 12.5911C15.5591 12.6229 15.5376 12.6535 15.5173 12.6853C15.491 12.7247 15.4648 12.7629 15.4385 12.8024C15.4206 12.8265 15.4015 12.8494 15.3836 12.8736C15.3609 12.9029 15.3394 12.9322 15.3179 12.9614C15.3179 12.9614 15.3167 12.9627 15.3155 12.964C15.2642 13.0174 15.2153 13.0734 15.1591 13.1218C15.1174 13.1587 15.0744 13.1981 15.0207 13.2198C14.9896 13.2325 14.9598 13.2363 14.9252 13.2274C14.8846 13.2172 14.8559 13.193 14.8309 13.1625C14.8165 13.1447 14.8106 13.1192 14.7998 13.0989C14.7867 13.076 14.795 13.0518 14.7915 13.0289C14.7867 12.9907 14.801 12.9576 14.8189 12.9245C14.8368 12.8914 14.8535 12.8584 14.8726 12.8253C14.8917 12.7935 14.9132 12.7629 14.9335 12.7324C14.9443 12.7146 14.9538 12.6955 14.9658 12.6777C14.9825 12.6509 15.0004 12.6242 15.0147 12.5975C15.0601 12.5122 15.1042 12.427 15.1484 12.3417C15.1532 12.3328 15.158 12.3239 15.1627 12.3162C15.1102 12.2679 15.0577 12.2183 15.0051 12.1699C14.9849 12.1508 14.9646 12.1343 14.9478 12.1126C14.9192 12.0757 14.881 12.0452 14.8655 11.9994C14.8559 11.9726 14.8428 11.9459 14.8464 11.9141C14.8488 11.895 14.8464 11.8747 14.8464 11.8543C14.8464 11.8238 14.8595 11.797 14.875 11.7716C14.8846 11.755 14.8989 11.7423 14.9108 11.7283C14.9395 11.6965 14.9777 11.68 15.0183 11.6672C15.0219 11.6672 15.0242 11.6621 15.0266 11.6596H15.1651L15.1723 11.6583Z"
                        fill="#3D3C3D"
                      />
                      <path
                        d="M12.4251 11.6596C12.4346 11.6647 12.4454 11.6711 12.4549 11.6749C12.4907 11.6889 12.5242 11.708 12.548 11.7372C12.5683 11.7614 12.585 11.7881 12.5958 11.8187C12.6101 11.8607 12.6053 11.9027 12.6006 11.9421C12.597 11.9765 12.5815 12.0096 12.56 12.0401C12.5015 12.119 12.4263 12.1839 12.357 12.2539C12.3355 12.2755 12.3093 12.2921 12.2866 12.3124C12.3272 12.3939 12.3678 12.474 12.4084 12.5529C12.4239 12.5822 12.4418 12.6089 12.4585 12.6369C12.4859 12.6815 12.5122 12.7273 12.5397 12.7718C12.5612 12.8075 12.5815 12.8444 12.6029 12.88C12.6173 12.9042 12.6328 12.9283 12.6447 12.9551C12.6519 12.9703 12.6567 12.9894 12.6579 13.006C12.6602 13.0212 12.6579 13.0378 12.6579 13.0543C12.6602 13.09 12.6447 13.1192 12.628 13.1485C12.6053 13.188 12.5731 13.2147 12.5265 13.2287C12.4848 13.2427 12.4477 13.235 12.4131 13.2147C12.3797 13.1943 12.3475 13.1701 12.3164 13.1447C12.2758 13.1103 12.2352 13.0734 12.197 13.0352C12.1708 13.0085 12.1493 12.978 12.1254 12.95C12.1111 12.9334 12.0956 12.9169 12.0824 12.9004C12.0621 12.8736 12.0419 12.8456 12.0216 12.8176C12.006 12.796 11.9881 12.7756 11.9738 12.7527C11.9595 12.7324 11.9464 12.7133 11.9332 12.6929C11.9129 12.6624 11.8914 12.6306 11.8735 12.5988C11.8628 12.5784 11.852 12.5822 11.8365 12.5898C11.8138 12.6026 11.79 12.614 11.7661 12.6229C11.7267 12.6395 11.6861 12.6547 11.6467 12.67C11.6133 12.6827 11.581 12.6967 11.5488 12.7069C11.519 12.7171 11.4879 12.7235 11.4557 12.7336C11.445 12.7375 11.4318 12.7375 11.4235 12.7438C11.4175 12.7489 11.4151 12.7604 11.4151 12.7693C11.4151 12.7782 11.4199 12.7871 11.4211 12.796C11.4247 12.8304 11.4223 12.866 11.4306 12.8991C11.4366 12.9207 11.4342 12.9423 11.4378 12.964C11.4414 12.9894 11.4473 13.0149 11.4533 13.0391C11.4533 13.0391 11.4533 13.0416 11.4533 13.0429C11.4557 13.0747 11.4593 13.1065 11.4617 13.1383C11.4641 13.1689 11.4617 13.202 11.4712 13.2312C11.482 13.2656 11.476 13.2987 11.4784 13.333C11.4796 13.3725 11.4796 13.4107 11.4784 13.4501C11.4784 13.4781 11.4772 13.5086 11.47 13.5366C11.4569 13.5914 11.4235 13.6334 11.3697 13.655C11.3351 13.669 11.2981 13.6639 11.2623 13.6639C11.2145 13.6639 11.1716 13.6461 11.1393 13.6117C11.1155 13.5875 11.0987 13.5557 11.0964 13.5188C11.0964 13.4959 11.0868 13.4743 11.082 13.4526C11.082 13.4488 11.082 13.4463 11.082 13.4425C11.0773 13.4157 11.0713 13.389 11.0665 13.3636C11.0665 13.3597 11.0665 13.3572 11.0665 13.3534C11.0617 13.3279 11.0558 13.3012 11.051 13.2758C11.0474 13.2579 11.045 13.2389 11.0426 13.221C11.0379 13.2007 11.0331 13.179 11.0283 13.1587C11.0247 13.1421 11.0223 13.1256 11.0188 13.1103C11.0116 13.0849 11.0044 13.0582 10.9973 13.0327C10.9937 13.0212 10.9901 13.0085 10.9877 12.9971C10.9818 12.9703 10.977 12.9436 10.971 12.9182C10.9674 12.9029 10.9603 12.8876 10.9567 12.8736C10.9543 12.866 10.9567 12.8571 10.9555 12.8494C10.9555 12.8329 10.94 12.8253 10.9245 12.8316C10.9197 12.8342 10.9137 12.8367 10.9089 12.8367C10.8254 12.8405 10.7406 12.8431 10.6558 12.8444C10.6021 12.8444 10.5484 12.8393 10.4959 12.8367C10.4827 12.8367 10.4756 12.838 10.4744 12.8545C10.4744 12.8711 10.466 12.8864 10.4613 12.9016C10.4577 12.9131 10.4541 12.9258 10.4517 12.9373C10.4457 12.9614 10.4422 12.9856 10.435 13.0085C10.4254 13.0391 10.4147 13.0696 10.4051 13.1014C10.4016 13.1141 10.398 13.1281 10.3944 13.1409C10.3872 13.1689 10.3777 13.1956 10.3705 13.2236C10.3634 13.2478 10.3598 13.2732 10.3538 13.2974C10.3443 13.3305 10.3335 13.3636 10.3228 13.3967C10.3156 13.4208 10.3108 13.445 10.3049 13.4692C10.3001 13.4883 10.2941 13.5061 10.287 13.5252C10.2798 13.5443 10.2714 13.5634 10.2643 13.5824C10.2583 13.5977 10.2535 13.6143 10.2452 13.6283C10.2285 13.6588 10.1986 13.6779 10.1676 13.6944C10.1282 13.7148 10.0864 13.7097 10.0458 13.711C10.0148 13.711 9.98613 13.7033 9.96344 13.683C9.94792 13.669 9.93121 13.6537 9.91808 13.6372C9.91092 13.6283 9.90972 13.6143 9.90614 13.6028C9.90375 13.5926 9.90017 13.5812 9.89898 13.571C9.89898 13.5672 9.89898 13.5621 9.89898 13.557C9.90136 13.543 9.90495 13.5277 9.90614 13.5137C9.90972 13.4832 9.91211 13.4539 9.9145 13.4246C9.91569 13.4068 9.91927 13.3903 9.92285 13.3725C9.92763 13.3508 9.9336 13.3292 9.93837 13.3076C9.93837 13.305 9.93837 13.3025 9.93837 13.2999C9.94434 13.2732 9.94912 13.2452 9.95389 13.2185C9.95747 13.2007 9.95986 13.1816 9.96225 13.1638C9.96702 13.1371 9.97299 13.1103 9.97777 13.0849V13.0785C9.98016 13.0543 9.98135 13.0289 9.98612 13.0047C9.99448 12.9703 9.99926 12.936 10.0028 12.9004C10.0064 12.8685 10.0172 12.8367 10.0255 12.8062C10.0327 12.782 10.0184 12.7667 9.99209 12.7642C9.9718 12.7616 9.9527 12.7553 9.9336 12.7502C9.90853 12.7438 9.88465 12.7387 9.85958 12.7324C9.84884 12.7298 9.83929 12.7273 9.82854 12.7235C9.77602 12.7056 9.72468 12.6878 9.67215 12.6687C9.63634 12.656 9.60172 12.642 9.56591 12.6306C9.56113 12.6293 9.54919 12.6369 9.54561 12.642C9.52174 12.6802 9.49905 12.7184 9.47518 12.7553C9.45727 12.7833 9.44056 12.8125 9.42146 12.8393C9.40474 12.8634 9.38445 12.8864 9.36654 12.9105C9.34386 12.9398 9.32237 12.9691 9.29969 12.9983C9.29014 13.0111 9.27939 13.0238 9.26865 13.0365C9.24358 13.0696 9.21851 13.1014 9.19344 13.1345C9.1815 13.1498 9.16837 13.1625 9.15762 13.1778C9.13972 13.202 9.12539 13.2287 9.1051 13.2503C9.08958 13.2669 9.08122 13.2859 9.06093 13.2999C9.04063 13.3139 9.02034 13.3267 8.99885 13.333C8.98214 13.3381 8.96423 13.3343 8.94632 13.3356C8.92006 13.3368 8.89499 13.333 8.86992 13.3216C8.82814 13.3025 8.80068 13.2707 8.78158 13.2299C8.78158 13.2274 8.7768 13.2261 8.77441 13.2249V13.1103C8.778 13.104 8.78158 13.0963 8.78516 13.09C8.81859 13.0276 8.85321 12.9653 8.88663 12.9029C8.8926 12.8927 8.89499 12.8825 8.90096 12.8724C8.91528 12.8482 8.9308 12.8253 8.94513 12.8011C8.95826 12.7807 8.969 12.7578 8.98214 12.7375C8.99885 12.712 9.01676 12.6878 9.03347 12.6624C9.05735 12.6242 9.07883 12.586 9.10271 12.5479C9.12539 12.5109 9.14927 12.4753 9.17314 12.4397C9.16359 12.432 9.15763 12.4257 9.15046 12.4206C9.13017 12.4066 9.10749 12.3939 9.08719 12.3799C9.0657 12.3646 9.04421 12.3493 9.02273 12.3341C8.99169 12.3112 8.95826 12.2908 8.92961 12.2653C8.89738 12.2373 8.86992 12.2043 8.84246 12.1724C8.82217 12.1483 8.80426 12.1203 8.78635 12.0935C8.78396 12.091 8.78038 12.0834 8.7768 12.0757V11.979C8.78158 11.9714 8.78516 11.9625 8.78993 11.9548C8.80545 11.9243 8.82575 11.8963 8.8544 11.8747C8.88186 11.853 8.91409 11.8518 8.94393 11.8441C8.9511 11.8428 8.96065 11.8492 8.969 11.8518C8.98333 11.8556 9.00004 11.8581 9.01437 11.8658C9.05973 11.8874 9.1039 11.9103 9.14808 11.9332C9.16837 11.9434 9.18866 11.9561 9.20896 11.9676C9.24716 11.9917 9.28417 12.0146 9.32237 12.0388C9.35221 12.0566 9.38325 12.0757 9.41429 12.091C9.46801 12.1177 9.52293 12.1432 9.57784 12.1686C9.60769 12.1826 9.63873 12.1966 9.66977 12.2093C9.69722 12.2195 9.72468 12.2272 9.75214 12.2373C9.78915 12.2501 9.82616 12.2628 9.86436 12.2742C9.88585 12.2819 9.90853 12.2857 9.93002 12.2921C9.96464 12.3022 9.99806 12.3137 10.0327 12.3213C10.0637 12.329 10.096 12.3315 10.1282 12.3379C10.164 12.3455 10.1998 12.3544 10.2356 12.3608C10.2762 12.3684 10.3168 12.3735 10.3586 12.3773C10.4075 12.3811 10.4565 12.385 10.5054 12.385C10.5532 12.385 10.5997 12.3862 10.6463 12.385C10.6917 12.3837 10.7358 12.3901 10.7812 12.3786C10.8146 12.3697 10.8516 12.3722 10.8863 12.3697C10.9173 12.3671 10.9483 12.3646 10.9794 12.3608C10.9973 12.3582 11.014 12.3557 11.0319 12.3519C11.0534 12.3481 11.0761 12.3417 11.0987 12.3366C11.1095 12.3341 11.119 12.3302 11.1298 12.329C11.1477 12.3252 11.1668 12.3239 11.1835 12.3201C11.1978 12.3175 11.2122 12.3112 11.2265 12.3061C11.248 12.2997 11.2707 12.2946 11.2921 12.2882C11.316 12.2819 11.3399 12.2755 11.3626 12.2679C11.3865 12.2603 11.4103 12.2501 11.4354 12.2412C11.4736 12.2272 11.513 12.2144 11.55 12.1966C11.6312 12.1572 11.7148 12.1203 11.79 12.0668C11.8282 12.0388 11.8699 12.0159 11.9081 11.9892C11.9368 11.9688 11.9643 11.9472 11.9905 11.9243C12.0263 11.8937 12.061 11.8619 12.0956 11.8288C12.1063 11.8187 12.1123 11.8059 12.123 11.7945C12.1433 11.7741 12.1684 11.7576 12.1827 11.7347C12.1982 11.7092 12.2209 11.6965 12.2448 11.6825C12.2603 11.6736 12.277 11.666 12.2926 11.6583H12.4298L12.4251 11.6596Z"
                        fill="#3D3C3D"
                      />
                      <path
                        d="M14.9109 15.5971C14.998 15.6645 15.0159 15.7943 14.9514 15.8872C14.7378 16.1913 14.493 16.3861 14.234 16.4891C13.9749 16.5922 13.7147 16.6011 13.4723 16.5553C12.9936 16.4649 12.5734 16.1608 12.331 15.9114C12.2535 15.8312 12.2511 15.7001 12.3275 15.6187C12.4027 15.5372 12.528 15.5347 12.6056 15.6149C12.8121 15.8274 13.1655 16.0768 13.5416 16.1481C13.7266 16.1824 13.914 16.1748 14.0943 16.1023C14.2746 16.0297 14.4596 15.8897 14.6351 15.6403C14.6996 15.5487 14.8225 15.5296 14.9097 15.5971H14.9109Z"
                        fill="#3D3C3D"
                      />
                    </svg>
                    <h2
                      class="holiday-text-primary !font-galano !text-base !font-medium !normal-case !leading-tight text-galaxy xl:!text-lg"
                    >
                      Top Results for Sleep
                    </h2>
                  </div>
                `
              },
              item({ item, components, html }) {
                const distinct = window.algoliaShopify.config.show_products
                const variantInfo = !distinct && item.objectID !== item.id ? `?variant=${item.objectID}` : ''
                const itemLink = `${window.Shopify.routes.root}products/${item.handle}/${variantInfo}`
                return createAutocompleteProductCard(
                  item,
                  html,
                  window.algoliaShopify.helpers,
                  itemLink,
                  window.algoliaShopify.helpers.handleItemClick
                )
              },
            },
          },
        ]
      },
    }

    const playResultsRuleContexts = ['play-search-results', visitorStatus, ...utmParams]
    const playSearchResultsPlugin = {
      name: 'custom-play-search-results-plugin',
      getSources({ query }) {
        return [
          {
            sourceId: 'playSearchResults',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: 'shopify_products',
                    query,
                    params: {
                      filters: `${BASE_PRODUCT_FILTER} AND named_tags.division:play`,
                      hitsPerPage: 4,
                      ruleContexts: playResultsRuleContexts,
                      analyticsTags,
                    },
                  },
                ],
              })
            },
            templates: {
              header({ html, state }) {
                return html`
                  <div class="mx-4 !mb-6 flex gap-2 xl:mx-0 xl:!mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="23"
                      viewBox="0 0 40 23"
                      fill="none"
                    >
                      <path
                        d="M37.91 22.6731H2.0866C1.7366 22.6731 1.45459 22.3664 1.45459 21.9915C1.45459 21.6169 1.73912 21.3101 2.0866 21.3101H37.91C38.2601 21.3101 38.542 21.6169 38.542 21.9915C38.542 22.3664 38.2575 22.6731 37.91 22.6731Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M19.8205 12.6374C19.4706 12.6374 19.1885 12.3305 19.1885 11.9558V1.7953C19.1885 1.41788 19.4731 1.11377 19.8205 1.11377C20.168 1.11377 20.4525 1.4206 20.4525 1.7953V11.9531C20.4525 12.3305 20.168 12.6346 19.8205 12.6346V12.6374Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M25.7031 15.434C25.5419 15.434 25.3784 15.3661 25.2549 15.233C25.0081 14.9669 25.0081 14.5352 25.2549 14.2691L32.9776 5.94139C33.2243 5.6753 33.6248 5.6753 33.8716 5.94139C34.1183 6.20749 34.1183 6.63921 33.8716 6.90531L26.1489 15.233C26.0254 15.3661 25.8642 15.434 25.7005 15.434H25.7031Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M24.1567 10.0524C24.0762 10.0524 23.993 10.0361 23.915 10.0008C23.5927 9.85686 23.4391 9.45771 23.5725 9.10744L26.1862 2.31657C26.3196 1.96902 26.6898 1.80339 27.0147 1.94729C27.3369 2.0912 27.4906 2.49035 27.3572 2.84062L24.7434 9.6315C24.6428 9.89488 24.406 10.0524 24.1592 10.0524H24.1567Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M29.3644 19.5129C29.0372 19.5129 28.7601 19.2413 28.7349 18.8857C28.7072 18.5109 28.9666 18.1797 29.3142 18.1498L39.3156 17.2755C39.6681 17.2456 39.9703 17.5253 39.9979 17.9C40.0256 18.2747 39.7664 18.606 39.4188 18.6358L29.4174 19.5102C29.3998 19.5102 29.3822 19.5102 29.3644 19.5102V19.5129Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M30.6937 15.8166C30.4345 15.8166 30.1903 15.6428 30.0971 15.3632C29.9787 15.0075 30.1498 14.6165 30.4798 14.4916L37.1323 11.9284C37.4622 11.8007 37.8248 11.9854 37.9405 12.3411C38.0589 12.6968 37.8877 13.0878 37.5578 13.2127L30.9053 15.7759C30.8348 15.803 30.7617 15.8166 30.6912 15.8166H30.6937Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M14.1783 15.434C14.0171 15.434 13.8534 15.3661 13.7301 15.233L6.00733 6.90531C5.76058 6.63921 5.76058 6.20749 6.00733 5.94139C6.2541 5.6753 6.65446 5.6753 6.90123 5.94139L14.6239 14.2691C14.8706 14.5352 14.8706 14.9669 14.6239 15.233C14.5006 15.3661 14.3394 15.434 14.1757 15.434H14.1783Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M15.2479 10.0524C14.9987 10.0524 14.7644 9.89488 14.6638 9.6315L12.0502 2.84062C11.9165 2.49306 12.0703 2.09392 12.3925 1.94729C12.7148 1.80339 13.085 1.96902 13.221 2.31657L15.8346 9.10744C15.9681 9.455 15.8145 9.85414 15.4922 10.0008C15.4142 10.0361 15.331 10.0524 15.2505 10.0524H15.2479Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M10.6355 19.5123C10.6179 19.5123 10.6003 19.5123 10.5826 19.5123L0.581173 18.638C0.233692 18.6082 -0.0256582 18.2769 0.00202538 17.9022C0.0297285 17.5275 0.336924 17.2478 0.684404 17.2777L10.6859 18.152C11.0333 18.1819 11.2927 18.5131 11.265 18.8878C11.2372 19.2435 10.9604 19.5151 10.6355 19.5151V19.5123Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M8.71151 15.8166C8.64098 15.8166 8.56811 15.803 8.49758 15.7759L1.84501 13.2127C1.51515 13.0851 1.34393 12.6941 1.46229 12.3411C1.58062 11.9854 1.9407 11.8007 2.27056 11.9284L8.92309 14.4916C9.25287 14.6192 9.42421 15.0102 9.30582 15.3632C9.21263 15.6428 8.97096 15.8166 8.70897 15.8166H8.71151Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M9.68066 21.6032C9.68066 15.4507 14.2536 10.4597 19.8974 10.4597C25.541 10.4597 30.1139 15.4475 30.1139 21.6032"
                        fill="#F9CB63"
                      />
                      <path
                        d="M28.614 22.6728C28.176 22.6728 27.8234 22.2898 27.8234 21.8201C27.8234 17.063 24.2353 13.191 19.8213 13.191C15.4072 13.191 11.8191 17.0603 11.8191 21.8201C11.8191 22.2926 11.4641 22.6728 11.0284 22.6728C10.5928 22.6728 10.2378 22.2898 10.2378 21.8201C10.2378 16.1208 14.5361 11.4858 19.8213 11.4858C25.1064 11.4858 29.4047 16.1208 29.4047 21.8201C29.4047 22.2926 29.0497 22.6728 28.614 22.6728Z"
                        fill="#F9CB63"
                      />
                      <path
                        d="M22.5524 16.5208C22.5524 16.5208 22.575 16.5316 22.5852 16.5398C22.6079 16.5533 22.6305 16.5669 22.6481 16.5859C22.6784 16.6158 22.7085 16.6484 22.7311 16.6864C22.7665 16.7434 22.8243 16.776 22.8646 16.8276C22.8872 16.8547 22.9175 16.8765 22.9453 16.8982C22.9779 16.9226 23.0107 16.947 23.0433 16.9688C23.0635 16.9823 23.0812 16.9959 23.1014 17.0095C23.1088 17.0149 23.1139 17.0176 23.1215 17.0231C23.1869 17.0611 23.2524 17.0991 23.3204 17.1371C23.3581 17.1561 23.3985 17.1697 23.4388 17.186C23.4739 17.1996 23.5067 17.2159 23.5419 17.2294C23.5646 17.2376 23.5847 17.243 23.6074 17.2484C23.6376 17.2566 23.6654 17.2674 23.6955 17.2756C23.7132 17.281 23.7334 17.281 23.751 17.2865C23.7685 17.2892 23.7838 17.2973 23.7988 17.3027C23.8014 17.3027 23.8039 17.3027 23.8064 17.3027C23.8314 17.3055 23.8566 17.3082 23.8844 17.3136C23.9096 17.319 23.9373 17.3245 23.9625 17.3299C23.9651 17.3299 23.9674 17.3299 23.97 17.3299C24.0178 17.3326 24.0631 17.3299 24.111 17.3408C24.2192 17.3598 24.33 17.3462 24.4384 17.3489C24.4787 17.3489 24.5164 17.3435 24.5566 17.3408C24.5793 17.3408 24.5994 17.3353 24.6221 17.3326C24.66 17.3272 24.6977 17.3218 24.7354 17.3136C24.7758 17.3055 24.8161 17.2946 24.8538 17.2865C24.8892 17.2783 24.9269 17.2702 24.962 17.2593C25.0025 17.2457 25.0427 17.2294 25.083 17.2159C25.1056 17.2077 25.1283 17.2023 25.1484 17.1941C25.1685 17.1887 25.1861 17.1806 25.2064 17.1724C25.2467 17.1561 25.2869 17.1398 25.3272 17.1208C25.3448 17.1127 25.3625 17.1018 25.3801 17.0937C25.4305 17.0665 25.4833 17.0421 25.5337 17.0122C25.5665 16.9932 25.5966 16.9742 25.6294 16.9525C25.6697 16.9253 25.7074 16.8982 25.7478 16.871C25.7679 16.8574 25.788 16.8439 25.8082 16.833C25.8535 16.8059 25.9014 16.7787 25.9492 16.7543C25.9617 16.7461 25.9793 16.7434 25.9946 16.7407C26.0045 16.738 26.0147 16.7325 26.0221 16.7325C26.0399 16.7325 26.06 16.738 26.075 16.7461C26.0952 16.7543 26.1178 16.7651 26.1331 16.7814C26.1506 16.8004 26.1657 16.8249 26.1759 16.8493C26.2034 16.9199 26.2011 16.9905 26.1657 17.0584C26.1581 17.0747 26.1456 17.0882 26.1354 17.0991C26.1053 17.1344 26.0776 17.1724 26.0448 17.205C26.0096 17.2403 25.9693 17.2674 25.9314 17.3C25.9088 17.319 25.8861 17.338 25.8635 17.3543C25.8433 17.3706 25.8207 17.3842 25.8005 17.3978C25.7931 17.4032 25.7855 17.4114 25.7753 17.4195C25.8005 17.4602 25.8258 17.501 25.8484 17.5444C25.8635 17.5716 25.8787 17.6014 25.8963 17.6286C25.9139 17.6585 25.9314 17.6856 25.9492 17.7155C25.9617 17.7372 25.9719 17.7589 25.9844 17.7806C26.002 17.8105 26.0198 17.8404 26.0348 17.873C26.0827 17.9707 26.1331 18.0685 26.1784 18.1662C26.1858 18.1798 26.1858 18.1961 26.1884 18.2124C26.1884 18.2341 26.1884 18.2558 26.1884 18.2775C26.1884 18.3101 26.1784 18.3373 26.1606 18.3644C26.143 18.397 26.1178 18.416 26.0877 18.4296C26.0725 18.4377 26.0549 18.4432 26.0373 18.4432C26.0045 18.4432 25.9719 18.4513 25.9391 18.435C25.9291 18.4296 25.9189 18.4296 25.9113 18.4242C25.8838 18.3997 25.856 18.3753 25.8408 18.34C25.8334 18.3237 25.8207 18.3101 25.8107 18.2965C25.7906 18.2667 25.7705 18.2341 25.7501 18.2042C25.7402 18.1906 25.7326 18.1771 25.7226 18.1662C25.7074 18.1472 25.6898 18.1282 25.6773 18.1065C25.6521 18.0739 25.6294 18.0386 25.6067 18.0033C25.5966 17.9897 25.5892 17.9761 25.579 17.9653C25.5589 17.9381 25.5388 17.911 25.5212 17.8838C25.5011 17.8539 25.4833 17.8186 25.4657 17.7888C25.4456 17.7535 25.4229 17.7182 25.4028 17.6829C25.3977 17.6747 25.3952 17.6639 25.3901 17.6557C25.3852 17.6449 25.3801 17.6367 25.3625 17.6449C25.3297 17.6612 25.2971 17.672 25.2643 17.6856C25.2391 17.6965 25.2139 17.7073 25.1887 17.7155C25.1635 17.7236 25.1408 17.7318 25.1158 17.7399C25.0855 17.7508 25.0527 17.7589 25.0226 17.7698C25.0099 17.7752 24.9949 17.7806 24.9824 17.7833C24.9646 17.7888 24.947 17.7888 24.9269 17.7942C24.9144 17.7969 24.9017 17.8024 24.8892 17.8051C24.894 17.8295 24.8966 17.8512 24.9017 17.8757C24.9067 17.8974 24.9118 17.9218 24.9167 17.9436C24.9167 17.9463 24.9167 17.949 24.9167 17.9517C24.9192 17.9816 24.9218 18.0141 24.9243 18.044C24.9269 18.0685 24.9345 18.0929 24.9394 18.1173V18.1228C24.9419 18.1499 24.9445 18.1798 24.947 18.2069C24.9495 18.2286 24.9571 18.2477 24.962 18.2694C24.962 18.2748 24.962 18.2775 24.962 18.283C24.9671 18.3128 24.9722 18.3427 24.9773 18.3726V18.378C24.9798 18.4051 24.9822 18.435 24.9847 18.4622C24.9898 18.5056 24.9923 18.5491 25.0025 18.5898C25.0099 18.6197 25.0074 18.6495 25.0099 18.6767C25.0099 18.6821 25.0124 18.6875 25.015 18.693C25.015 18.6984 25.0201 18.7038 25.0175 18.7065C25.0124 18.7337 25.0099 18.7609 24.9999 18.7853C24.9824 18.8233 24.9521 18.8504 24.9118 18.864C24.869 18.8803 24.8286 18.8722 24.7858 18.8695C24.7606 18.8695 24.7354 18.8477 24.7104 18.8342C24.675 18.8151 24.66 18.7798 24.6424 18.7445C24.6323 18.7228 24.6272 18.7011 24.6198 18.6767C24.6121 18.6522 24.6045 18.6305 24.5971 18.6061C24.592 18.5898 24.5895 18.5762 24.5844 18.5599C24.5768 18.5328 24.5693 18.5056 24.5617 18.4758C24.5592 18.4622 24.5541 18.4486 24.5518 18.435C24.5441 18.4024 24.534 18.3698 24.5266 18.3373C24.5189 18.3074 24.5164 18.2775 24.5088 18.2477C24.5039 18.2205 24.4937 18.1961 24.4861 18.1689C24.4787 18.1445 24.4736 18.1173 24.466 18.0929C24.4635 18.082 24.4611 18.0712 24.4586 18.0603C24.451 18.0277 24.4408 17.9951 24.4334 17.9625C24.4257 17.9354 24.4207 17.9055 24.4132 17.873C24.4082 17.873 24.4005 17.873 24.3931 17.873C24.3074 17.8757 24.2218 17.8838 24.1362 17.8811C24.0784 17.8784 24.0229 17.8757 23.9651 17.873C23.9625 17.873 23.96 17.873 23.9575 17.873C23.9272 17.8594 23.9221 17.8648 23.9197 17.9001C23.9197 17.9191 23.9096 17.9381 23.907 17.9599C23.9045 17.9734 23.8994 17.987 23.8971 18.0033C23.892 18.0332 23.8869 18.0657 23.8793 18.0956C23.8744 18.1228 23.8643 18.1472 23.8541 18.1716C23.8492 18.1852 23.8467 18.1961 23.8441 18.2097C23.8416 18.2314 23.8391 18.2558 23.834 18.2775C23.8291 18.2992 23.824 18.321 23.8189 18.3427C23.8164 18.359 23.8113 18.3753 23.8088 18.3916C23.8064 18.416 23.8039 18.4405 23.7988 18.4649C23.7963 18.4866 23.7887 18.5111 23.7838 18.5328C23.7838 18.5328 23.7838 18.5355 23.7838 18.5382C23.7812 18.5653 23.7838 18.5952 23.7736 18.6224C23.7635 18.6631 23.7611 18.7038 23.7359 18.7418C23.7107 18.7798 23.6753 18.7961 23.6402 18.8124C23.5999 18.8287 23.5571 18.8233 23.5167 18.8179C23.464 18.8097 23.4212 18.7771 23.396 18.7228C23.3833 18.693 23.3833 18.6604 23.3784 18.6278C23.3631 18.5409 23.3733 18.4567 23.3708 18.3699C23.3708 18.3318 23.3758 18.2965 23.3784 18.2585C23.3784 18.2341 23.3833 18.2124 23.3858 18.1879C23.3909 18.1391 23.3934 18.0875 23.4034 18.0413C23.411 18.0141 23.4059 17.987 23.411 17.9599C23.4161 17.93 23.4212 17.8974 23.4261 17.8675C23.4261 17.8567 23.4261 17.8458 23.4261 17.8377C23.4261 17.8241 23.4311 17.8105 23.4337 17.7996C23.4362 17.7861 23.4337 17.7725 23.4212 17.7698C23.3985 17.7616 23.3758 17.7562 23.3557 17.7508C23.3254 17.7399 23.2977 17.7318 23.2674 17.7209C23.2373 17.71 23.2096 17.6992 23.1818 17.6883C23.1541 17.6775 23.1266 17.6639 23.1014 17.6503C23.0635 17.6313 23.0257 17.6096 22.9855 17.5906C22.9779 17.5879 22.9705 17.5851 22.9628 17.5987C22.9453 17.6367 22.9226 17.672 22.8999 17.7073C22.8722 17.7535 22.847 17.7969 22.8167 17.8404C22.7991 17.8675 22.779 17.8947 22.7589 17.9218C22.7362 17.9544 22.7136 17.9897 22.6909 18.0223C22.6909 18.0223 22.6909 18.0223 22.6883 18.025C22.6354 18.0847 22.5852 18.1499 22.5272 18.2069C22.4844 18.2504 22.4391 18.2938 22.3838 18.3183C22.3509 18.3318 22.3207 18.3373 22.2855 18.3264C22.2427 18.3128 22.2124 18.2857 22.1872 18.2531C22.1722 18.2314 22.1671 18.2042 22.1546 18.1798C22.1419 18.1526 22.1495 18.1255 22.147 18.0983C22.1419 18.0549 22.1571 18.0169 22.1747 17.9789C22.1923 17.9408 22.2099 17.9028 22.23 17.8648C22.2501 17.8295 22.2728 17.7942 22.2931 17.7589C22.3056 17.7399 22.3158 17.7182 22.3257 17.6965C22.3433 17.6666 22.3611 17.6367 22.3762 17.6041C22.424 17.5064 22.4693 17.4087 22.5147 17.3109C22.5197 17.3 22.5246 17.2919 22.5297 17.281C22.4744 17.224 22.4215 17.1697 22.366 17.1127C22.3459 17.091 22.3232 17.072 22.3056 17.0475C22.2753 17.0068 22.2376 16.9688 22.2201 16.9172C22.2099 16.8873 22.1974 16.8547 22.1999 16.8194C22.1999 16.7977 22.1999 16.7733 22.1999 16.7515C22.1999 16.7162 22.215 16.6864 22.23 16.6565C22.2402 16.6375 22.2552 16.6239 22.2679 16.6076C22.298 16.5696 22.3384 16.5533 22.3787 16.537C22.3812 16.537 22.3838 16.5316 22.3861 16.5289C22.434 16.5289 22.4818 16.5289 22.5297 16.5289L22.5524 16.5208Z"
                        fill="#403940"
                      />
                      <path
                        d="M20.1029 20.048C20.0853 20.048 20.0677 20.048 20.0501 20.048C19.6094 20.0236 19.2015 19.6733 19.0807 19.2144C19.0555 19.1248 19.1059 19.0298 19.189 19.0026C19.272 18.9782 19.3601 19.0298 19.3853 19.1194C19.4709 19.4398 19.7581 19.6869 20.0677 19.7059C20.3748 19.7249 20.6872 19.5104 20.8054 19.2008C20.8383 19.114 20.9314 19.0705 21.0119 19.1085C21.0926 19.1438 21.1329 19.2443 21.0975 19.3312C20.9365 19.7548 20.5235 20.048 20.1029 20.048Z"
                        fill="#403940"
                      />
                    </svg>
                    <h2
                      class="holiday-text-primary !font-galano !text-base !font-medium !normal-case !leading-tight text-galaxy xl:!text-lg"
                    >
                      Top Results for Play
                    </h2>
                  </div>
                `
              },
              item({ item, components, html }) {
                const distinct = window.algoliaShopify.config.show_products
                const variantInfo = !distinct && item.objectID !== item.id ? `?variant=${item.objectID}` : ''
                const itemLink = `${window.Shopify.routes.root}products/${item.handle}/${variantInfo}`
                if (item.available === false) {
                  return html``
                }
                return createAutocompleteProductCard(
                  item,
                  html,
                  window.algoliaShopify.helpers,
                  itemLink,
                  window.algoliaShopify.helpers.handleItemClick
                )
              },
            },
          },
        ]
      },
    }

    options.plugins.push(filteredCollectionsPlugin)
    options.plugins.push(trendingProductsPlugin)
    options.plugins.push(sleepSearchResultsPlugin)
    options.plugins.push(playSearchResultsPlugin)

    return options
  })

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteNoResultsTemplate',
    function (_defaultTemplate, templateOptions) {
      const { html, state } = templateOptions
      return html`
        <div
          class="aa-PanelLayout hide-scrollbar aa-Panel--scrollable max-w-7xl xl:!mx-auto xl:px-8 xxxl:max-w-[1440px] xxxl:!px-0"
        >
          <h1 class="sr-only">Search Results</h1>
          <div class="aa-PanelSections !flex-col-reverse !gap-0 xl:w-full xl:!flex-row xl:!gap-10">
            <div class="aa-PanelSection--left xl:!w-[300px] xl:!min-w-[300px]">${staticLinks(html)}</div>
            <div class="aa-PanelSection--right xl:flex xl:grow xl:flex-col">
              <div class="mx-4 !mb-4 flex gap-2 ag:mx-0">
                <p class="!font-galano text-xs font-medium leading-tight text-galaxy xl:text-lg">No results found</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteMainProductsTemplate',
    function (_defaultTemplate, templateOptions, elements) {
      const { html, state } = templateOptions

      return html`
        <div
          class="aa-PanelLayout hide-scrollbar aa-Panel--scrollable max-w-7xl xl:!mx-auto xl:px-8 xxxl:max-w-[1440px] xxxl:!px-0"
        >
          <h1 class="sr-only">Search Results</h1>
          <div class="aa-PanelSections !flex-col-reverse !gap-0 xl:w-full xl:!flex-row xl:!gap-10">
            <div class="aa-PanelSection--left xl:!w-[300px] xl:!min-w-[300px]">${staticLinks(html)}</div>
            <div class="aa-PanelSection--right xl:flex xl:grow xl:flex-col">
              <div class="mx-4 !mb-4 flex gap-2 ag:mx-0">
                <p class="!font-galano text-xs font-medium leading-tight text-galaxy xl:text-lg">No results found</p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteMainTemplate',
    function (_defaultTemplate, templateOptions, elements, displaySuggestions) {
      const { html, state } = templateOptions
      const isEmptyQuery = !state.query
      const query = templateOptions.state.query

      if (isEmptyQuery) {
        return html`
          <div
            class="aa-PanelLayout hide-scrollbar aa-Panel--scrollable max-w-7xl xl:!mx-auto xl:px-8 xxxl:max-w-[1440px] xxxl:!px-0"
          >
            <h1 class="sr-only">Search Results</h1>
            <div class="aa-PanelSections !flex-col-reverse !gap-0 xl:w-full xl:!flex-row xl:!gap-10">
              <div class="aa-PanelSection--left xl:!w-[300px] xl:!min-w-[300px]">
                <h2
                  class="holiday-text-primary mb-4 ml-4 mt-8 !font-galano !text-xs !font-bold !normal-case !leading-tight text-galaxy xl:ml-0 xl:mt-0 xl:!text-sm"
                >
                  Top Searches
                </h2>
                <div class="px-4 xl:px-0">${elements.querySuggestionsPlugin}</div>
                ${staticLinks(html)}
              </div>
              <div class="aa-PanelSection--right xl:flex xl:grow xl:flex-col">
                <div>
                  <div class="mx-4 !mb-4 flex gap-2 ag:mx-0">
                    <h2
                      class="holiday-text-primary !font-galano !text-xs !font-bold !normal-case !leading-tight text-galaxy xl:!text-lg xl:!font-medium"
                    >
                      Top Trending Products
                    </h2>
                  </div>
                  <div class="w-screen overflow-hidden ag:w-auto">${elements.trendingProducts}</div>
                </div>
              </div>
            </div>
          </div>
        `
      }

      // Return normal search results template for non-empty queries
      return html`
        <div
          class="aa-PanelLayout hide-scrollbar aa-Panel--scrollable max-w-7xl overflow-y-auto overflow-x-visible ag:!mx-auto ag:w-full ag:!px-8 ag:!pb-12"
        >
          <h1 class="sr-only">Search Results</h1>

          <div class="aa-PanelSections relative ag:!gap-10">
            <div class="aa-PanelSections ag:w-full ag:!gap-10">
              <div class="aa-PanelSection--left ag:!w-[300px] ag:!min-w-[300px]">
                ${elements.filteredCollections || elements.articles || elements.pages || elements.redirectUrlPlugin
                  ? html`
                      <div class="px-4 ag:mb-8 ag:px-0">
                        ${elements.filteredCollections} ${elements.articles} ${elements.pages}
                        ${elements.redirectUrlPlugin}
                      </div>
                    `
                  : ''}
                <a
                  href="/search?q=${query}"
                  class="holiday-button-primary group hidden items-center justify-center gap-2.5 rounded-full border-2 border-solid border-[#403940] px-10 py-3 transition-colors hover:bg-galaxy ag:flex"
                >
                  <span
                    class="holiday-text-white text-center !font-galano text-lg font-semibold leading-tight text-[#403940] transition-colors group-hover:text-white"
                    >View All Results</span
                  >
                </a>
              </div>
              <div class="aa-PanelSection--right ag:grow">
                ${elements.sleepSearchResults ? elements.sleepSearchResults : ''}
                ${elements.playSearchResults ? elements.playSearchResults : ''}
              </div>
              <a
                href="/search?q=${query}"
                class="group mx-4 mt-6 block items-center justify-center gap-2.5 rounded-full border-2 border-solid border-[#403940] bg-galaxy px-12 py-4 transition-colors hover:bg-white ag:mx-0 ag:hidden"
              >
                <span
                  class="block w-full text-center !font-galano text-lg font-semibold leading-tight text-white transition-colors group-hover:text-[#403940]"
                  >View All Results</span
                >
              </a>
            </div>
          </div>
        </div>
      `
    }
  )

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    function (_defaultTemplate, templateOptions, distinct, itemLink, trackSearchAttribution) {
      // Modify default options, then return them
      const { html, item } = templateOptions

      return createAutocompleteProductCard(item, html, algoliaShopify.helpers, itemLink, trackSearchAttribution)
    }
  )

  algoliaShopify.hooks.registerHook('beforeInstantSearchNoResultTemplate', function (_defaultTemplate, html) {
    const collectionTitle = document.querySelector('[data-collection-title]').textContent
    const query = window.algoliaShopify.instantsearch.search.renderState.shopify_products.searchBox.query

    return html`
      <div class="ais-hit-empty">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="26"
          height="28"
          viewBox="0 0 26 28"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.2337 3C7.41019 3 3.5 6.91019 3.5 11.7337C3.5 16.5571 7.41019 20.4673 12.2337 20.4673C17.0571 20.4673 20.9673 16.5571 20.9673 11.7337C20.9673 6.91019 17.0571 3 12.2337 3ZM0.5 11.7337C0.5 5.25334 5.75334 0 12.2337 0C18.714 0 23.9673 5.25334 23.9673 11.7337C23.9673 14.9782 22.6504 17.9152 20.522 20.0392L25.0615 24.5863C25.6468 25.1726 25.646 26.1224 25.0597 26.7077C24.4735 27.293 23.5237 27.2922 22.9384 26.7059L18.1243 21.8837C16.3931 22.8906 14.3807 23.4673 12.2337 23.4673C5.75334 23.4673 0.5 18.214 0.5 11.7337Z"
            fill="#CCE8F2"
          />
          <path
            d="M12.542 6.10986C14.586 6.10986 15.958 7.38386 15.958 9.17586C15.958 10.7159 14.95 11.6679 13.424 11.9479V13.3619H11.296V10.3939H12.416C13.2 10.3939 13.746 9.90386 13.746 9.21786C13.746 8.57386 13.228 8.06986 12.514 8.06986C11.772 8.06986 11.296 8.53186 11.17 9.28786L9 8.85386C9.182 7.32786 10.484 6.10986 12.542 6.10986ZM12.332 14.5519C13.074 14.5519 13.606 15.1259 13.606 15.7979C13.606 16.4979 13.074 17.0299 12.332 17.0299C11.576 17.0299 11.044 16.4979 11.044 15.7979C11.044 15.1259 11.576 14.5519 12.332 14.5519Z"
            fill="#CCE8F2"
          />
        </svg>
        <p class="!my-4 !font-galano !text-2xl font-medium !leading-tight text-galaxy">
          0 Results for "${query}" within ${collectionTitle}
        </p>
        <a
          href="https://littlesleepies.com/search?q=${query}"
          class="!mx-auto block w-fit rounded-full border border-solid border-[#403940] !bg-[#403940] !px-7 !py-2.5 !text-white transition-colors hover:bg-white hover:text-[#403940]"
        >
          <span class="font-galano text-sm font-bold leading-tight">Search the Site</span>
        </a>
      </div>
    `
  })
})
